import React, { useState, useEffect } from 'react';
import { 
  Users, 
  CheckSquare, 
  ExternalLink, 
  MessageSquare, 
  Send, 
  Clock, 
  FileText, 
  CheckCircle, 
  AlertCircle, 
  Plus, 
  Layout, 
  Layers, 
  Briefcase, 
  FileUp, 
  UserCheck,
  Folder
} from 'lucide-react';

interface TaskItem {
  id: string;
  text: string;
  completed: boolean;
}

interface WorkerTasks {
  Mahmood: TaskItem[];
  Anjum: TaskItem[];
  Nazia: TaskItem[];
}

export default function NexoraXRemoteWorkplace() {
  // Staff Access Gate State
  const [workerSession, setWorkerSession] = useState<{
    worker: string;
    role: string;
    accessLevel: string;
    loginTime: string;
  } | null>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("nexorax_remote_worker_session");
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {}
      }
    }
    return null;
  });

  const [accessCode, setAccessCode] = useState('');
  const [accessError, setAccessError] = useState('');

  const handleAccessSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const code = accessCode.trim().toUpperCase();
    
    let worker = '';
    let role = '';
    let accessLevel = '';

    if (code === 'MAHMOOD-ADMIN') {
      worker = 'Mahmood';
      role = 'Owner / Final Approval';
      accessLevel = 'ADMIN';
    } else if (code === 'ANJUM-LEADS') {
      worker = 'Anjum';
      role = 'Leads & Outreach';
      accessLevel = 'LEADS';
    } else if (code === 'NAZIA-CANVA') {
      worker = 'Nazia';
      role = 'Canva Campaigns & TikTok Content';
      accessLevel = 'CANVA';
    } else {
      setAccessError('Access code not recognised. Please contact Mahmood.');
      return;
    }

    const newSession = {
      worker,
      role,
      accessLevel,
      loginTime: new Date().toISOString()
    };

    localStorage.setItem("nexorax_remote_worker_session", JSON.stringify(newSession));
    setWorkerSession(newSession);
    setAccessCode('');
    setAccessError('');
  };

  const handleLogout = () => {
    localStorage.removeItem("nexorax_remote_worker_session");
    setWorkerSession(null);
  };

  // State for task completion (interactive checklist)
  const [tasks, setTasks] = useState<WorkerTasks>({
    Anjum: [
      { id: 'a1', text: 'Add 20–30 local business leads', completed: false },
      { id: 'a2', text: 'Send 20 outreach messages', completed: false },
      { id: 'a3', text: 'Record replies in Outreach Log', completed: false }
    ],
    Nazia: [
      { id: 'n1', text: 'Prepare 2 Canva campaign drafts', completed: false },
      { id: 'n2', text: 'Prepare 2 TikTok captions', completed: false },
      { id: 'n3', text: 'Prepare hashtags', completed: false },
      { id: 'n4', text: 'Add Canva links for review', completed: false }
    ],
    Mahmood: [
      { id: 'm1', text: 'Approve campaigns', completed: false },
      { id: 'm2', text: 'Review interested leads', completed: false },
      { id: 'm3', text: 'Send payment links', completed: false },
      { id: 'm4', text: 'Approve final posting', completed: false }
    ]
  });

  // Submission Form State
  const [submitForm, setSubmitForm] = useState({
    workerName: 'Anjum',
    workType: 'Lead List',
    businessName: '',
    notes: '',
    status: 'Ready for Review'
  });
  
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Message Board State
  const [messages, setMessages] = useState([
    { id: 1, sender: 'Anjum', time: '10:15 AM', content: 'Lead list started for Edinburgh roofing campaign.' },
    { id: 2, sender: 'Nazia', time: '11:30 AM', content: 'Canva draft ready for review: /pricing Table Tent decals.' },
    { id: 3, sender: 'Mahmood', time: '12:05 PM', content: 'Approved Anjum\'s leads. Nazia, needs small changes on the font sizing.' }
  ]);

  const [newMessage, setNewMessage] = useState('');

  // REMOTE_WORKPLACE_FRONTEND_SERVER_PATCH
  const [serverTasks, setServerTasks] = useState<any[]>([]);
  const [serverSubmissions, setServerSubmissions] = useState<any[]>([]);
  const [serverMessages, setServerMessages] = useState<any[]>([]);
  const [serverActivity, setServerActivity] = useState<any[]>([]);
  const [serverNotice, setServerNotice] = useState('');

  const [remoteTaskForm, setRemoteTaskForm] = useState({
    title: '',
    assignedTo: 'Anjum',
    assignedRole: 'LEADS',
    category: 'Lead Discovery',
    priority: 'High',
    instructions: '',
    dueDate: ''
  });

  const [serverWorkForm, setServerWorkForm] = useState({
    workType: 'Lead List',
    businessOrCampaignName: '',
    deliverableLinkOrNotes: '',
    status: 'Ready for Review'
  });

  const [serverMessageText, setServerMessageText] = useState('');
  const [adminReviewNotes, setAdminReviewNotes] = useState<Record<string, string>>({});

  // REMOTE_WORKPLACE_BIG_FINAL_POLISH_PATCH
  const [showArchivedSubmissions, setShowArchivedSubmissions] = useState(false);

  // SMART_QUOTE_ADMIN_PANEL_PHASE2
  const [smartQuotes, setSmartQuotes] = useState<any[]>([]);
  const [smartQuoteNotes, setSmartQuoteNotes] = useState<Record<string, string>>({});

  // QR_CAMPAIGN_REMOTE_WORKPLACE_PANEL
  const [qrCampaigns, setQrCampaigns] = useState<any[]>([]);
  const [qrCampaignNotes, setQrCampaignNotes] = useState<Record<string, string>>({});

  // CAMPAIGN_LAUNCH_REMOTE_WORKPLACE_PANEL
  const [launchCampaigns, setLaunchCampaigns] = useState<any[]>([]);
  const [launchCampaignNotes, setLaunchCampaignNotes] = useState<Record<string, string>>({});

  // OPS_ASSISTANT_FRONTEND_PATCH
  const [opsInput, setOpsInput] = useState('');
  const [opsLoading, setOpsLoading] = useState(false);

  // OPS_PREMIUM_VOICE_UI_PATCH
  const [opsSpeaking, setOpsSpeaking] = useState(false);
  const [voiceMode, setVoiceMode] = useState<'female' | 'standard'>('female');
  const [opsMessages, setOpsMessages] = useState<Array<{ role: 'assistant' | 'user'; content: string }>>([
    {
      role: 'assistant',
      content: 'Welcome to NexoraX Ops Assistant. Ask me for help with outreach, Canva captions, TikTok descriptions, hashtags, lead checks or task instructions.'
    }
  ]);

  const handleAskOpsAssistant = async (e: React.FormEvent) => {
    e.preventDefault();

    const question = opsInput.trim();
    if (!question || opsLoading) return;

    const workerName = workerSession?.worker || 'Worker';
    const workerRole = workerSession?.role || 'Remote Worker';
    const accessLevel = workerSession?.accessLevel || 'WORKER';

    const memoryContext = `
NexoraX Remote Workplace context:
Worker name: ${workerName}
Worker role: ${workerRole}
Access level: ${accessLevel}

Current workplace purpose:
Remote campaign, outreach, Canva tasks, leads and client work hub.

Role guidance:
- Anjum focuses on leads, outreach messages, lead checks and outreach logs.
- Nazia focuses on Canva campaign drafts, TikTok captions, hashtags and social media packs.
- Mahmood handles approvals, payment links, final review and posting.

Safety:
Do not request or reveal passwords, banking details, TikTok login, Tide login, Gmail login, domain admin or private credentials.
`;

    setOpsMessages(prev => [...prev, { role: 'user', content: question }]);
    setOpsInput('');
    setOpsLoading(true);

    try {
      const response = await fetch('/api/nexorax-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId: 'ops-assistant',
          message: question,
          memories: memoryContext
        })
      });

      const data = await response.json();
      const reply = data.reply || data.response || data.text || data.message || 'NexoraX Ops Assistant could not reply. Please try again.';

      setOpsMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (error) {
      setOpsMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: 'NexoraX Ops Assistant is temporarily unavailable. Please try again, or escalate this to Mahmood.'
        }
      ]);
    } finally {
      setOpsLoading(false);
    }
  };


  // OPS_ASSISTANT_SUGGESTION_CHIPS_PATCH
  const opsSuggestions = (() => {
    if (workerSession?.accessLevel === 'LEADS') {
      return [
        'Write an outreach message for a local business offering the £49 Digital Health Check.',
        'Check this lead and tell me if it looks worth contacting.',
        'Write a follow-up message for a business that has not replied.',
        'Create a short call script for asking about website and social media problems.',
        'Explain what details I should collect for this lead.',
        'Score this business lead from 1 to 10 and tell me if I should contact them.',
        'Write a short WhatsApp follow-up for a business owner.',
        'Create a 3-step outreach plan for this niche.',
        'Write a professional message I can send without sounding pushy.'
      ];
    }

    if (workerSession?.accessLevel === 'CANVA') {
      return [
        'Write a TikTok caption for a NexoraX business campaign.',
        'Create hashtags for a UK small business digital products video.',
        'Suggest on-screen text for a 7-clip Canva campaign.',
        'Check if this campaign idea is strong enough before I design it.',
        'Write a short premium voiceover script for Canva.',
        'Create 5 on-screen text lines for a luxury NexoraX video.',
        'Turn this rough idea into a polished TikTok description.',
        'Make this campaign sound more premium and less basic.',
        'Create a NEX20 code reveal end-card script.'
      ];
    }

    return [
      'Review this submitted work and tell me if it needs changes.',
      'Create today’s task list for Anjum and Nazia.',
      'Write an approval note or changes request for a campaign draft.',
      'Suggest the best quick offer to sell to a local business.',
      'Create a follow-up plan to get payment or deposit.'
    ];
  })();


  // OPS_ASSISTANT_PRODUCTIVITY_CONTROLS_PATCH
  const getLastAssistantReply = () => {
    const assistantReplies = opsMessages.filter((msg) => msg.role === 'assistant');
    return assistantReplies[assistantReplies.length - 1]?.content || '';
  };

  const copyLastAssistantReply = async () => {
    const text = getLastAssistantReply();
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setOpsMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: 'Copied the latest assistant response. You can now paste it into the Command Centre, Canva, TikTok caption box or message log.'
        }
      ]);
    } catch {
      setOpsMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: 'Copy failed in this browser. Please highlight the response manually and copy it.'
        }
      ]);
    }
  };

  const speakLastAssistantReply = () => {
    const text = getLastAssistantReply();
    if (!text || typeof window === 'undefined' || !('speechSynthesis' in window)) {
      setOpsMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: 'Voice playback is not available in this browser. You can still copy or read the response.'
        }
      ]);
      return;
    }

    window.speechSynthesis.cancel();

    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = voices.find(v =>
      /sonia|libby|aria|jenny|samantha|zira|female|google uk english female|english female/i.test(v.name)
    ) || voices.find(v => /en-gb|en-us/i.test(v.lang)) || voices[0];

    const utterance = new SpeechSynthesisUtterance(text);
    if (voiceMode === 'female' && preferredVoice) {
      utterance.voice = preferredVoice;
    }

    utterance.rate = 0.9;
    utterance.pitch = voiceMode === 'female' ? 1.12 : 0.95;
    utterance.volume = 1;

    utterance.onstart = () => setOpsSpeaking(true);
    utterance.onend = () => setOpsSpeaking(false);
    utterance.onerror = () => setOpsSpeaking(false);

    window.speechSynthesis.speak(utterance);
  };

  const stopAssistantVoice = () => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setOpsSpeaking(false);
    }
  };

  const clearOpsAssistantChat = () => {
    setOpsMessages([
      {
        role: 'assistant',
        content: 'NexoraX Ops Assistant reset. Ask me for outreach help, Canva captions, TikTok descriptions, hashtags, lead checks or task instructions.'
      }
    ]);
  };


  // LEAD_DISCOVERY_ENGINE_PATCH
  const [leadScout, setLeadScout] = useState({
    trade: 'joiner',
    town: 'Dunfermline',
    batch: '1',
    notes: ''
  });

  const [leadScoutResults, setLeadScoutResults] = useState<Array<{
    title: string;
    priority: number;
    why: string;
    searchQuery: string;
    suggestedOffer: string;
    assistantPrompt: string;
  }>>([]);


  // OFFER_MATRIX_SAFE_PATCH
  const nexoraxOfferMatrix = [
    {
      name: 'Website LaunchCare',
      bestFor: 'no website, weak website, poor enquiry flow, outdated branding',
      pitch: 'professional website or landing page to turn visitors into enquiries'
    },
    {
      name: 'Mobile App / App-Style System',
      bestFor: 'takeaways, trades, salons, booking businesses and repeat-order companies',
      pitch: 'mobile-friendly app-style system for bookings, menus, customer actions and direct enquiries'
    },
    {
      name: 'CRM / Booking Dashboard',
      bestFor: 'businesses losing customer details, bookings, quotes, jobs or follow-ups',
      pitch: 'dashboard to track customers, bookings, leads, jobs and follow-ups in one place'
    },
    {
      name: 'QR Campaign Pack',
      bestFor: 'restaurants, takeaways, shops, menus, flyers, local offers and events',
      pitch: 'QR posters, flyers and scan-to-action campaigns that turn offline attention into online leads'
    },
    {
      name: 'AI Voice Assistant',
      bestFor: 'businesses missing calls, FAQs, bookings or enquiries outside working hours',
      pitch: 'voice or chat assistant to answer common questions and capture leads'
    },
    {
      name: 'Social Media Launch Pack',
      bestFor: 'businesses posting online but not getting enough enquiries',
      pitch: 'ready-made posts, captions, short video ideas and campaign assets'
    },
    {
      name: 'Business Email & Hosting',
      bestFor: 'businesses using personal emails or weak online setup',
      pitch: 'professional email, domain, hosting and setup support'
    },
    {
      name: 'Full Digital Launch Kit',
      bestFor: 'startups and growing companies wanting everything connected',
      pitch: 'website, branding, QR, social, dashboard, email, hosting and automation in one launch system'
    },
    {
      name: 'Remote Workplace OS',
      bestFor: 'companies with remote workers, sales teams, campaign staff or distributed operations',
      pitch: 'secure remote operations workspace with tasks, assistant guidance, logs, approvals and team controls'
    }
  ];

  const [selectedNexoraXOffer, setSelectedNexoraXOffer] = useState(nexoraxOfferMatrix[0].name);

  const selectedOfferDetails =
    nexoraxOfferMatrix.find((offer) => offer.name === selectedNexoraXOffer) || nexoraxOfferMatrix[0];

  const formatTitle = (value: string) => {
    const clean = value.trim();
    return clean ? clean.charAt(0).toUpperCase() + clean.slice(1) : 'Business';
  };

  const buildLeadDiscoveryResults = (e?: React.FormEvent) => {
    e?.preventDefault();

    const trade = leadScout.trade.trim() || 'local business';
    const town = leadScout.town.trim() || 'local area';
    const batchNumber = Math.max(1, Number(leadScout.batch) || 1);
    const start = ((batchNumber - 1) * 10) + 1;

    const resultTemplates = Array.from({ length: 10 }).map((_, index) => {
      const number = start + index;
      const priority = Math.max(72, 98 - (index * 3));
      const searchQuery = `${trade} ${town} reviews website facebook`;
      const suggestedOffer =
        priority >= 90
          ? 'Website / app preview + £49 Digital Health Check'
          : priority >= 82
            ? 'Social media + QR campaign improvement offer'
            : 'Digital presence audit and follow-up outreach';

      return {
        title: `${formatTitle(trade)} lead target ${number} — ${formatTitle(town)}`,
        priority,
        why: `Research this ${trade} in ${town}. Check whether they have a website, active Facebook/Instagram, Google reviews, booking options and clear contact details.`,
        searchQuery,
        suggestedOffer,
        assistantPrompt: `Lead Check: I am researching a ${trade} business in ${town}. Please help me score it, decide whether it is worth contacting, and suggest the best NexoraX offer. Notes: ${leadScout.notes || 'No notes yet.'}`
      };
    });

    setLeadScoutResults(resultTemplates);
  };

  const openLeadSearch = (type: 'google' | 'maps' | 'facebook') => {
    const trade = encodeURIComponent(leadScout.trade.trim() || 'local business');
    const town = encodeURIComponent(leadScout.town.trim() || 'local area');

    let url = `https://www.google.com/search?q=${trade}+${town}+business+website+reviews`;

    if (type === 'maps') {
      url = `https://www.google.com/maps/search/${trade}+${town}`;
    }

    if (type === 'facebook') {
      url = `https://www.facebook.com/search/pages/?q=${trade}%20${town}`;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const submitOpsAssistantPrompt = async (questionInput: string) => {
    const question = questionInput.trim();
    if (!question || opsLoading) return;

    const workerName = workerSession?.worker || 'Worker';
    const workerRole = workerSession?.role || 'Remote Worker';
    const accessLevel = workerSession?.accessLevel || 'WORKER';

    const memoryContext = `
NexoraX Remote Workplace context:
Worker name: ${workerName}
Worker role: ${workerRole}
Access level: ${accessLevel}

Selected NexoraX offer:
${selectedOfferDetails.name}
Best for: ${selectedOfferDetails.bestFor}
Pitch: ${selectedOfferDetails.pitch}

NexoraX offer menu:
${nexoraxOfferMatrix.map((offer) => `- ${offer.name}: ${offer.pitch}`).join('\n')}

Worker guidance:
- Anjum focuses on lead discovery, outreach, business checks and follow-up logs.
- Nazia focuses on Canva campaigns, TikTok captions, hashtags and campaign wording.
- Mahmood handles approvals, payment links, final review and posting.

Safety:
Do not request or reveal passwords, banking details, TikTok login, Tide login, Gmail login, domain admin or private credentials.
`;

    setOpsMessages(prev => [...prev, { role: 'user', content: question }]);
    setOpsInput('');
    setOpsLoading(true);

    try {
      const response = await fetch('/api/nexorax-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId: 'ops-assistant', message: question, memories: memoryContext })
      });

      const data = await response.json();
      const reply = data.reply || data.response || data.text || data.message || 'NexoraX Ops Assistant could not reply. Please try again.';

      setOpsMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch {
      setOpsMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: 'NexoraX Ops Assistant could not connect fully, but the lead has been loaded. Try again with the business type, town and selected offer.'
        }
      ]);
    } finally {
      setOpsLoading(false);
    }
  };

  const loadLeadIntoAssistant = (prompt: string) => {
    const upgradedPrompt = `${prompt}

Selected NexoraX offer:
${selectedOfferDetails.name}

Best fit:
${selectedOfferDetails.bestFor}

Pitch:
${selectedOfferDetails.pitch}

Please score this lead, explain whether this offer fits, and write a short outreach message with suggested next actions.`;

    setOpsInput(upgradedPrompt);
    submitOpsAssistantPrompt(upgradedPrompt);
  };


  const remoteWorkerPayload = () => ({
    workerName: workerSession?.worker || 'Unknown',
    workerRole: workerSession?.role || '',
    accessLevel: workerSession?.accessLevel || ''
  });

  const loadRemoteServerData = async (showRefreshNotice = false) => {
    try {
      const [tasksRes, submissionsRes, messagesRes, activityRes] = await Promise.all([
        fetch('/api/remote-workplace/tasks'),
        fetch('/api/remote-workplace/submissions'),
        fetch('/api/remote-workplace/messages'),
        fetch('/api/remote-workplace/activity')
      ]);

      const tasksData = await tasksRes.json();
      const submissionsData = await submissionsRes.json();
      const messagesData = await messagesRes.json();
      const activityData = await activityRes.json();

      setServerTasks(tasksData.tasks || []);
      setServerSubmissions(submissionsData.submissions || []);
      setServerMessages(messagesData.messages || []);
      setServerActivity(activityData.activity || []);

      if (showRefreshNotice) {
        setServerNotice(`Server data refreshed successfully at ${new Date().toLocaleTimeString()}.`);
      }
    } catch {
      setServerNotice('Server workflow data could not be loaded. Try refreshing.');
    }
  };

  const recordRemoteActivity = async (eventType: string, details: string) => {
    try {
      await fetch('/api/remote-workplace/activity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...remoteWorkerPayload(),
          eventType,
          details
        })
      });
    } catch {
      // silent fallback
    }
  };

  React.useEffect(() => {
    if (!workerSession) return;

    loadRemoteServerData();

    const loginKey = `nexorax_rw_login_logged_${workerSession.worker}_${workerSession.accessLevel}`;
    if (typeof sessionStorage !== 'undefined' && !sessionStorage.getItem(loginKey)) {
      recordRemoteActivity('Login', `${workerSession.worker} logged into Remote Workplace.`);
      sessionStorage.setItem(loginKey, '1');
    }
  }, [workerSession?.worker, workerSession?.accessLevel]);

  const visibleServerTasks = serverTasks.filter((task) => {
    if (workerSession?.accessLevel === 'ADMIN') return true;
    return (
      task.assignedTo?.toLowerCase() === workerSession?.worker?.toLowerCase() ||
      task.assignedRole === workerSession?.accessLevel
    );
  });

  const visibleServerSubmissions = serverSubmissions.filter((item) => {
    if (!showArchivedSubmissions && item.reviewStatus === 'Archived') return false;
    if (workerSession?.accessLevel === 'ADMIN') return true;
    return item.workerName?.toLowerCase() === workerSession?.worker?.toLowerCase();
  });

  const visibleServerActivity = serverActivity.filter((item) => {
    if (workerSession?.accessLevel === 'ADMIN') return true;
    return item.workerName?.toLowerCase() === workerSession?.worker?.toLowerCase();
  });

  const createRemoteTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (workerSession?.accessLevel !== 'ADMIN') return;

    try {
      const response = await fetch('/api/remote-workplace/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...remoteWorkerPayload(),
          ...remoteTaskForm,
          createdBy: workerSession?.worker || 'Mahmood'
        })
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Task creation failed.');

      setRemoteTaskForm({
        title: '',
        assignedTo: 'Anjum',
        assignedRole: 'LEADS',
        category: 'Lead Discovery',
        priority: 'High',
        instructions: '',
        dueDate: ''
      });

      setServerNotice('Task created and saved to server.');
      await loadRemoteServerData();
    } catch {
      setServerNotice('Task could not be created. Check required fields.');
    }
  };

  const updateRemoteTask = async (taskId: string, updates: any) => {
    try {
      const response = await fetch(`/api/remote-workplace/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...remoteWorkerPayload(),
          ...updates
        })
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Task update failed.');

      setServerNotice('Task updated.');
      await loadRemoteServerData();
    } catch {
      setServerNotice('Task update failed.');
    }
  };

  const submitServerWork = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!serverWorkForm.businessOrCampaignName.trim() || !serverWorkForm.deliverableLinkOrNotes.trim()) {
      setServerNotice('Please add a business/campaign name and work notes/link before submitting.');
      return;
    }

    try {
      const response = await fetch('/api/remote-workplace/submissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...remoteWorkerPayload(),
          ...serverWorkForm
        })
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Submission failed.');

      setServerWorkForm({
        workType: 'Lead List',
        businessOrCampaignName: '',
        deliverableLinkOrNotes: '',
        status: 'Ready for Review'
      });

      setServerNotice('Work submitted and saved for Mahmood review.');
      await loadRemoteServerData();
    } catch {
      setServerNotice('Work submission failed. Add a campaign name and notes/link.');
    }
  };

  const reviewServerSubmission = async (submissionId: string, reviewStatus: string) => {
    if (workerSession?.accessLevel !== 'ADMIN') return;

    try {
      const response = await fetch(`/api/remote-workplace/submissions/${submissionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...remoteWorkerPayload(),
          reviewStatus,
          adminNotes: adminReviewNotes[submissionId] || ''
        })
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Review failed.');

      setServerNotice(`Submission marked: ${reviewStatus}.`);
      await loadRemoteServerData();
    } catch {
      setServerNotice('Review update failed.');
    }
  };


  const deleteServerSubmission = async (submissionId: string) => {
    if (workerSession?.accessLevel !== 'ADMIN') return;
    if (!confirm('Delete this test submission permanently? Use Archive for normal old work.')) return;

    try {
      const response = await fetch(`/api/remote-workplace/submissions/${submissionId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(remoteWorkerPayload())
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Delete failed.');

      setServerNotice('Submission deleted.');
      await loadRemoteServerData();
    } catch {
      setServerNotice('Submission delete failed.');
    }
  };

  const copyDailyRemoteBrief = async () => {
    const openTasks = visibleServerTasks.filter((task) => !['Completed', 'Approved'].includes(task.status));
    const pendingReviews = visibleServerSubmissions.filter((item) => item.reviewStatus === 'Pending Review');

    const brief = `NexoraX Remote Workplace Daily Brief

Open tasks: ${openTasks.length}
Pending reviews: ${pendingReviews.length}
Saved messages: ${serverMessages.length}
Activity events: ${serverActivity.length}

Top tasks:
${openTasks.slice(0, 5).map((task) => `- ${task.title} / ${task.assignedTo} / ${task.status}`).join('\n') || '- No open tasks'}

Pending review:
${pendingReviews.slice(0, 5).map((item) => `- ${item.businessOrCampaignName || 'Untitled'} / ${item.workerName}`).join('\n') || '- No pending reviews'}

Next action:
Review pending work, send changes if needed, approve strong work, and assign the next lead/campaign batch.`;

    try {
      await navigator.clipboard.writeText(brief);
      setServerNotice('Daily Remote Workplace brief copied.');
    } catch {
      setServerNotice('Could not copy brief. Browser clipboard blocked it.');
    }
  };


  const loadSmartQuotes = async () => {
    if (workerSession?.accessLevel !== 'ADMIN') return;

    try {
      const response = await fetch('/api/smart-quotes');
      const data = await response.json();

      if (data.success) {
        setSmartQuotes(Array.isArray(data.briefs) ? data.briefs : []);
      }
    } catch {
      setServerNotice('Smart Quote briefs could not be loaded.');
    }
  };

  const updateSmartQuoteStatus = async (quoteId: string, status: string) => {
    if (workerSession?.accessLevel !== 'ADMIN') return;

    try {
      const response = await fetch(`/api/smart-quotes/${quoteId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          adminNotes: smartQuoteNotes[quoteId] || ''
        })
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Update failed.');

      setServerNotice(`Smart Quote updated: ${status}`);
      await loadSmartQuotes();
    } catch {
      setServerNotice('Smart Quote update failed.');
    }
  };

  const createTaskFromSmartQuote = async (quote: any, taskType: 'lead' | 'creative') => {
    if (workerSession?.accessLevel !== 'ADMIN') return;

    const isCreative = taskType === 'creative';
    const assignedTo = isCreative ? 'Nazia' : 'Anjum';
    const category = isCreative ? 'Smart Quote Creative / Campaign' : 'Smart Quote Lead Research';
    const title = isCreative
      ? `Smart Quote Campaign Prep — ${quote.businessName || 'New Client'}`
      : `Smart Quote Lead Review — ${quote.businessName || 'New Client'}`;

    const instructions = isCreative
      ? `A Smart Quote brief has arrived.

Business: ${quote.businessName || ''}
Industry: ${quote.industry || ''}
Town: ${quote.town || ''}
Services requested: ${(quote.services || []).join(', ')}
Recommended offer: ${quote.recommendation?.mainService || 'Review required'}
Budget: ${quote.budget || ''}
Urgency: ${quote.urgency || ''}
Contact: ${quote.contactName || ''} / ${quote.email || ''} / ${quote.phone || ''}

Task:
Prepare a campaign angle, caption idea, Canva direction and social/QR concept for this client. Do not contact the client. Submit your draft notes to the server for Mahmood review.`
      : `A Smart Quote brief has arrived.

Business: ${quote.businessName || ''}
Industry: ${quote.industry || ''}
Town: ${quote.town || ''}
Website: ${quote.currentWebsite || 'Not provided'}
Social: ${quote.socialLink || 'Not provided'}
Services requested: ${(quote.services || []).join(', ')}
Recommended offer: ${quote.recommendation?.mainService || 'Review required'}
Budget: ${quote.budget || ''}
Urgency: ${quote.urgency || ''}
Contact: ${quote.contactName || ''} / ${quote.email || ''} / ${quote.phone || ''}
Goals: ${quote.goals || ''}

Task:
Research this business. Check website, Google, Facebook/social presence, reviews, contact details and best NexoraX offer fit. Do not contact the client. Submit a short research report to the server for Mahmood review.`;

    try {
      const response = await fetch('/api/remote-workplace/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...remoteWorkerPayload(),
          title,
          assignedTo,
          priority: quote.urgency === 'ASAP' || quote.urgency === 'This week' ? 'High' : 'Medium',
          category,
          dueDate: 'Today',
          instructions
        })
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Task creation failed.');

      await updateSmartQuoteStatus(quote.id, 'Under Review');
      await loadRemoteServerData();
      setServerNotice(`${assignedTo} task created from Smart Quote.`);
    } catch {
      setServerNotice('Could not create task from Smart Quote.');
    }
  };



  const loadQrCampaigns = async () => {
    if (workerSession?.accessLevel !== 'ADMIN') return;

    try {
      const response = await fetch('/api/qr-campaigns');
      const data = await response.json();

      if (data.success) {
        const campaigns = Array.isArray(data.campaigns) ? data.campaigns : [];
        setQrCampaigns(campaigns);
        setServerNotice(`QR campaigns loaded inside Remote Workplace. ${campaigns.length} campaign(s) loaded.`);
      }
    } catch {
      setServerNotice('QR campaigns could not be loaded.');
    }
  };

  const updateQrCampaignStatus = async (campaignId: string, status: string) => {
    if (workerSession?.accessLevel !== 'ADMIN') return;

    try {
      const response = await fetch(`/api/qr-campaigns/${campaignId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          adminNotes: qrCampaignNotes[campaignId] || ''
        })
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'QR update failed.');

      setServerNotice(`QR Campaign updated: ${status}`);
      await loadQrCampaigns();
    } catch {
      setServerNotice('QR Campaign update failed.');
    }
  };

  const createTaskFromQrCampaign = async (campaign: any, taskType: 'lead' | 'creative') => {
    if (workerSession?.accessLevel !== 'ADMIN') return;

    const isCreative = taskType === 'creative';
    const assignedTo = isCreative ? 'Nazia' : 'Anjum';
    const category = isCreative ? 'QR Campaign Creative / Canva' : 'QR Campaign Lead Research';
    const title = isCreative
      ? `QR Campaign Design Prep — ${campaign.businessName || 'New Campaign'}`
      : `QR Campaign Lead Check — ${campaign.businessName || 'New Campaign'}`;

    const campaignData = campaign.campaign || {};

    const instructions = isCreative
      ? `A QR Campaign has been saved for review.

Reference: ${campaign.reference || ''}
Business: ${campaign.businessName || ''}
Business type: ${campaign.businessType || ''}
Town: ${campaign.town || ''}
Goal: ${campaign.goal || ''}
Offer: ${campaign.offerTitle || ''}
Destination: ${campaign.destination || campaign.destinationUrl || ''}
CTA: ${campaignData.cta || ''}
Poster copy: ${campaignData.posterCopy || ''}
TikTok caption: ${campaignData.tikTokCaption || ''}

Task:
Prepare Canva/poster direction, short caption options, QR campaign wording and social launch idea. Do not post anything publicly. Submit draft notes or Canva link to the server for Mahmood review.`
      : `A QR Campaign has been saved for review.

Reference: ${campaign.reference || ''}
Business: ${campaign.businessName || ''}
Business type: ${campaign.businessType || ''}
Town: ${campaign.town || ''}
Goal: ${campaign.goal || ''}
Offer: ${campaign.offerTitle || ''}
Destination: ${campaign.destination || campaign.destinationUrl || ''}
CTA: ${campaignData.cta || ''}
Best use: ${campaignData.bestUse || ''}

Task:
Research this business/campaign opportunity. Check whether the QR campaign angle fits the business, identify where QR posters/flyers could be used, and suggest the best NexoraX offer. Do not contact the client. Submit a short research report to the server for Mahmood review.`;

    // DUPLICATE_QR_TASK_GUARD
    if (taskAlreadyExists(title, campaign.reference)) {
      setServerNotice('This QR Campaign task already exists. Duplicate task was blocked.');
      return;
    }

    try {
      const response = await fetch('/api/remote-workplace/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...remoteWorkerPayload(),
          title,
          assignedTo,
          priority: 'Medium',
          category,
          dueDate: 'Today',
          instructions
        })
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Task creation failed.');

      await updateQrCampaignStatus(campaign.id, 'Under Review');
      await loadRemoteServerData();
      setServerNotice(`${assignedTo} task created from QR Campaign.`);
    } catch {
      setServerNotice('Could not create task from QR Campaign.');
    }
  };



  const loadLaunchCampaigns = async () => {
    if (workerSession?.accessLevel !== 'ADMIN') return;

    try {
      const response = await fetch('/api/campaign-launches');
      const data = await response.json();

      if (data.success) {
        setLaunchCampaigns(Array.isArray(data.campaigns) ? data.campaigns : []);
      }
    } catch {
      setServerNotice('Launch campaigns could not be loaded.');
    }
  };

  const updateLaunchCampaignStatus = async (campaignId: string, status: string) => {
    if (workerSession?.accessLevel !== 'ADMIN') return;

    try {
      const response = await fetch(`/api/campaign-launches/${campaignId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          notes: launchCampaignNotes[campaignId] || ''
        })
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Campaign update failed.');

      setServerNotice(`Launch campaign updated: ${status}`);
      await loadLaunchCampaigns();
    } catch {
      setServerNotice('Launch campaign update failed.');
    }
  };

  const createTaskFromLaunchCampaign = async (campaign: any, taskType: 'video' | 'tracking') => {
    if (workerSession?.accessLevel !== 'ADMIN') return;

    const isVideo = taskType === 'video';
    const assignedTo = isVideo ? 'Nazia' : 'Anjum';
    const category = isVideo ? 'TikTok Video / Caption Prep' : 'TikTok Campaign Tracking / Lead Check';
    const title = isVideo
      ? `TikTok Video Prep — ${campaign.campaignName || 'Launch Campaign'}`
      : `TikTok Campaign Tracking — ${campaign.campaignName || 'Launch Campaign'}`;

    const instructions = isVideo
      ? `A TikTok launch campaign has been prepared.

Reference: ${campaign.reference || ''}
Campaign: ${campaign.campaignName || ''}
Platform: ${campaign.platform || ''}
Objective: ${campaign.objective || ''}
Landing page: ${campaign.landingPage || ''}
Tracking URL: ${campaign.trackingUrl || ''}
Video type: ${campaign.videoType || ''}
Video link: ${campaign.videoLink || 'Not added yet'}
Caption: ${campaign.caption || ''}
Hashtags: ${campaign.hashtags || ''}
Daily budget: £${campaign.dailyBudget || ''}
Total budget: £${campaign.totalBudget || ''}
Status: ${campaign.status || ''}

Task:
Prepare or check the TikTok video/caption pack for this campaign. Make sure the video matches the Smart Quote / NexoraX offer, keep it professional, and submit the final Canva/Drive/TikTok video link to the server. Do not publish or spend money without Mahmood approval.`
      : `A TikTok launch campaign has been prepared.

Reference: ${campaign.reference || ''}
Campaign: ${campaign.campaignName || ''}
Platform: ${campaign.platform || ''}
Objective: ${campaign.objective || ''}
Landing page: ${campaign.landingPage || ''}
Tracking URL: ${campaign.trackingUrl || ''}
Audience: ${campaign.audience || ''}
Daily budget: £${campaign.dailyBudget || ''}
Total budget: £${campaign.totalBudget || ''}
Current spend: £${campaign.spend || '0'}
Impressions: ${campaign.impressions || '0'}
Clicks: ${campaign.clicks || '0'}
Leads: ${campaign.leads || '0'}

Task:
Check the campaign setup, landing page and tracking URL. When the campaign is live, record daily spend, impressions, clicks, CTR, CPC and leads. Do not change payment settings or spend money without Mahmood approval. Submit a short daily tracking report to the server.`;

    // DUPLICATE_LAUNCH_TASK_GUARD
    if (taskAlreadyExists(title, campaign.reference)) {
      setServerNotice('This Launch Pad task already exists. Duplicate task was blocked.');
      return;
    }

    try {
      const response = await fetch('/api/remote-workplace/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...remoteWorkerPayload(),
          title,
          assignedTo,
          priority: 'High',
          category,
          dueDate: 'Tomorrow',
          instructions
        })
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Task creation failed.');

      await updateLaunchCampaignStatus(campaign.id, 'Ready for Review');
      await loadRemoteServerData();
      setServerNotice(`${assignedTo} task created from Launch Pad campaign.`);
    } catch {
      setServerNotice('Could not create task from Launch Pad campaign.');
    }
  };



  // TASK_DELETE_AND_DUPLICATE_GUARD_PATCH
  const deleteRemoteTask = async (taskId: string, taskTitle?: string) => {
    if (workerSession?.accessLevel !== 'ADMIN') {
      setServerNotice('Only Mahmood admin can delete test tasks.');
      return;
    }

    const confirmed = window.confirm(`Delete this test task permanently?\n\n${taskTitle || taskId}`);
    if (!confirmed) return;

    try {
      const response = await fetch(`/api/remote-workplace/tasks/${taskId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Delete failed.');

      setServerNotice('Test task deleted successfully.');
      await loadRemoteServerData();
    } catch {
      setServerNotice('Could not delete this task.');
    }
  };

  const taskAlreadyExists = (title: string, reference?: string) => {
    const normalTitle = String(title || '').trim().toLowerCase();
    const normalReference = String(reference || '').trim().toLowerCase();

    return serverTasks.some((task: any) => {
      const taskTitle = String(task.title || '').trim().toLowerCase();
      const taskInstructions = String(task.instructions || '').trim().toLowerCase();

      return taskTitle === normalTitle || (!!normalReference && taskInstructions.includes(normalReference));
    });
  };


  const sendServerMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    const messageText = serverMessageText.trim();
    if (!messageText) return;

    try {
      const response = await fetch('/api/remote-workplace/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...remoteWorkerPayload(),
          messageText
        })
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Message failed.');

      setServerMessageText('');
      setServerNotice('Message saved to server board.');
      await loadRemoteServerData();
    } catch {
      setServerNotice('Message could not be sent.');
    }
  };

  const clearRemoteServerLogs = async () => {
    if (workerSession?.accessLevel !== 'ADMIN') return;
    if (!confirm('Clear all Remote Workplace tasks, submissions, messages and activity logs?')) return;

    try {
      const response = await fetch('/api/remote-workplace/logs', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(remoteWorkerPayload())
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Clear failed.');

      setServerNotice('Server logs cleared.');
      await loadRemoteServerData();
    } catch {
      setServerNotice('Could not clear logs.');
    }
  };

  const toggleTask = (worker: keyof WorkerTasks, id: string) => {
    setTasks(prev => ({
      ...prev,
      [worker]: prev[worker].map(t => t.id === id ? { ...t, completed: !t.completed } : t)
    }));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitSuccess(true);
    setTimeout(() => {
      setSubmitSuccess(false);
      setSubmitForm(prev => ({ ...prev, businessName: '', notes: '' }));
    }, 3000);
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setMessages(prev => [
      ...prev,
      {
        id: Date.now(),
        sender: 'Mahmood', // Mock sender
        time: 'Just now',
        content: newMessage
      }
    ]);
    setNewMessage('');
  };

  useEffect(() => {
    if (workerSession?.accessLevel === 'ADMIN') {
      loadSmartQuotes();
    }
  }, [workerSession?.accessLevel]);



  useEffect(() => {
    if (workerSession?.accessLevel === 'ADMIN') {
      loadQrCampaigns();
    }
  }, [workerSession?.accessLevel]);



  useEffect(() => {
    if (workerSession?.accessLevel === 'ADMIN') {
      loadLaunchCampaigns();
    }
  }, [workerSession?.accessLevel]);


  if (!workerSession) {
    return (
      <div className="min-h-screen bg-black text-slate-100 font-sans p-6 md:p-8 flex flex-col items-center justify-center selection:bg-purple-500 selection:text-white">
        {/* Main Card */}
        <div className="w-full max-w-md p-8 rounded-2xl bg-[#0b0c15] border border-purple-500/20 shadow-[0_0_50px_rgba(168,85,247,0.15)] flex flex-col items-center">
          
          {/* Logo / Icon */}
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-purple-600 to-cyan-500 flex items-center justify-center text-white font-black text-2xl tracking-widest shadow-[0_0_25px_rgba(0,240,255,0.35)] mb-6 animate-pulse">
            NX
          </div>

          {/* Heading */}
          <h1 className="text-xl md:text-2xl font-black uppercase text-center tracking-tight text-white mb-2">
            NexoraX Remote Workplace Access
          </h1>
          <p className="text-xs text-slate-400 text-center mb-6">
            Enter your staff access code to continue.
          </p>

          {/* Form */}
          <form onSubmit={handleAccessSubmit} className="w-full space-y-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-mono font-bold tracking-widest uppercase text-slate-400">
                Staff Access Code
              </label>
              <input
                type="password"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                placeholder="••••••••••••••"
                className="w-full px-4 py-3 rounded-xl bg-purple-950/20 border border-purple-500/30 text-white placeholder-slate-600 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(0,240,255,0.25)] text-center font-mono tracking-widest text-sm uppercase transition-all"
                autoFocus
              />
            </div>

            {/* Error Message */}
            {accessError && (
              <div className="p-3.5 rounded-xl bg-red-950/30 border border-red-500/30 text-center">
                <p className="text-xs font-medium text-red-400">
                  {accessError}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full py-3.5 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-xs font-black uppercase tracking-widest transition-all shadow-[0_0_20px_rgba(168,85,247,0.3)] cursor-pointer"
            >
              Verify Clearance
            </button>
          </form>

          {/* Copyright */}
          <div className="mt-8 text-[9px] font-mono text-slate-500 text-center uppercase tracking-widest">
            © NexoraX Digital LTD • Secured Gate
          </div>

        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-black text-slate-100 font-sans p-6 md:p-8 flex flex-col items-center selection:bg-cyan-500 selection:text-white">
      {/* Container */}
      <div className="w-full max-w-6xl space-y-8">
        
        {/* Route Active Banner */}
        <div className="w-full py-5 px-6 rounded-2xl bg-gradient-to-r from-purple-900 via-indigo-950 to-purple-900 border-2 border-cyan-400 text-center shadow-[0_0_30px_rgba(0,240,255,0.4)] mb-2">
          <h2 className="text-2xl md:text-3xl font-black tracking-[0.25em] text-cyan-300 uppercase animate-pulse drop-shadow-[0_0_10px_rgba(0,240,255,0.6)]">
            ⚡ REMOTE WORKPLACE ROUTE ACTIVE ⚡
          </h2>
        </div>

        {/* Department Branding Header */}
        <header className="flex flex-col md:flex-row md:items-center justify-between border-b border-purple-500/20 pb-6 gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-600 to-cyan-500 flex items-center justify-center text-white font-black text-lg tracking-widest shadow-[0_0_15px_rgba(0,240,255,0.3)]">
              NX
            </div>
            <div>
              <span className="text-xs font-mono font-black tracking-[0.25em] text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-indigo-400 to-[#e840f3]">
                NEXORAX DIGITAL LTD
              </span>
              <h1 className="text-2xl font-black uppercase tracking-tight text-white mt-0.5">
                Remote Workplace
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-purple-950/20 border border-purple-500/30 px-4 py-2 rounded-2xl shadow-[0_0_15px_rgba(168,85,247,0.1)]">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
            </span>
            <span className="text-[10px] font-mono font-bold tracking-widest uppercase text-cyan-400">
              {workerSession.worker} ({workerSession.accessLevel})
            </span>
            <button
              onClick={handleLogout}
              className="text-[9px] font-mono font-bold uppercase tracking-wider text-rose-400 hover:text-rose-300 ml-2 border-l border-purple-500/30 pl-2 hover:underline cursor-pointer"
            >
              Logout
            </button>
          </div>
        </header>

        {/* Subtitle / Overview */}
        <div className="bg-gradient-to-r from-purple-950/20 via-zinc-950/40 to-transparent border-l-2 border-cyan-400 p-5 rounded-r-2xl">
          <h2 className="text-lg font-black uppercase text-cyan-300">Campaigns & Client Operations</h2>
          <p className="text-sm text-slate-400 mt-1 leading-relaxed">
            Welcome to the secure team hub. Coordinate Canva tasks, scrape and qualify outreach leads, track campaign briefs, and submit work deliverables.
          </p>
        </div>

        {/* Section: Team & Responsibilities */}
        <section className="space-y-4">
          <h3 className="text-xs font-mono font-black uppercase tracking-widest text-slate-500 flex items-center gap-2">
            <Users size={12} className="text-purple-400" /> Team Node Directory
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Card: Mahmood */}
            <div className="relative group bg-[#020205] border border-zinc-900 hover:border-purple-500/40 rounded-2xl p-5 transition-all duration-300 hover:shadow-[0_0_20px_rgba(168,85,247,0.1)]">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="text-base font-black text-white">Mahmood</h4>
                  <span className="text-[10px] font-mono font-bold text-purple-400 uppercase tracking-wider block mt-0.5">Owner / Final Approval</span>
                </div>
                <UserCheck size={18} className="text-purple-500/50" />
              </div>
              <p className="text-xs text-slate-400 mt-4 leading-relaxed font-medium">
                Responsible for final validation, financial approvals, payment links routing, and live posting authorization.
              </p>
            </div>

            {/* Card: Anjum */}
            <div className="relative group bg-[#020205] border border-zinc-900 hover:border-cyan-500/40 rounded-2xl p-5 transition-all duration-300 hover:shadow-[0_0_20px_rgba(6,182,212,0.1)]">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="text-base font-black text-white">Anjum</h4>
                  <span className="text-[10px] font-mono font-bold text-cyan-400 uppercase tracking-wider block mt-0.5">Leads & Outreach</span>
                </div>
                <Users size={18} className="text-cyan-500/50" />
              </div>
              <p className="text-xs text-slate-400 mt-4 leading-relaxed font-medium">
                Drives prospect identification, qualifying leads, direct email/social outreach, and population of CRM queue databases.
              </p>
            </div>

            {/* Card: Nazia */}
            <div className="relative group bg-[#020205] border border-zinc-900 hover:border-indigo-500/40 rounded-2xl p-5 transition-all duration-300 hover:shadow-[0_0_20px_rgba(99,102,241,0.1)]">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="text-base font-black text-white">Nazia</h4>
                  <span className="text-[10px] font-mono font-bold text-indigo-400 uppercase tracking-wider block mt-0.5">Canva Campaigns & TikTok</span>
                </div>
                <Layers size={18} className="text-indigo-500/50" />
              </div>
              <p className="text-xs text-slate-400 mt-4 leading-relaxed font-medium">
                Crafts creative campaign visuals in Canva, designs layouts, writes captions, manages hashtags, and edits TikTok video files.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Today's Tasks */}
        <section className="space-y-4">
          <h3 className="text-xs font-mono font-black uppercase tracking-widest text-slate-500 flex items-center gap-2">
            <CheckSquare size={12} className="text-purple-400" /> Today's Action Checklist
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Tasks: Anjum */}
            <div className="bg-[#020205] border border-zinc-900 rounded-2xl p-5 space-y-4">
              <div className="border-b border-zinc-900 pb-2 flex justify-between items-center">
                <span className="text-xs font-bold text-white font-mono uppercase tracking-wider">Anjum's Queue</span>
                <span className="text-[9px] font-mono font-black uppercase text-cyan-400 bg-cyan-950/20 px-2 py-0.5 rounded">Leads</span>
              </div>
              <div className="space-y-3">
                {tasks.Anjum.map(task => (
                  <label key={task.id} className="flex items-start gap-3 text-xs font-medium cursor-pointer text-slate-300 hover:text-white select-none">
                    <input 
                      type="checkbox" 
                      checked={task.completed} 
                      onChange={() => toggleTask('Anjum', task.id)}
                      className="mt-0.5 h-3.5 w-3.5 rounded border-zinc-800 bg-black text-purple-600 focus:ring-0 focus:ring-offset-0" 
                    />
                    <span className={task.completed ? 'line-through text-slate-500' : ''}>{task.text}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Tasks: Nazia */}
            <div className="bg-[#020205] border border-zinc-900 rounded-2xl p-5 space-y-4">
              <div className="border-b border-zinc-900 pb-2 flex justify-between items-center">
                <span className="text-xs font-bold text-white font-mono uppercase tracking-wider">Nazia's Queue</span>
                <span className="text-[9px] font-mono font-black uppercase text-indigo-400 bg-indigo-955/20 px-2 py-0.5 rounded">Creative</span>
              </div>
              <div className="space-y-3">
                {tasks.Nazia.map(task => (
                  <label key={task.id} className="flex items-start gap-3 text-xs font-medium cursor-pointer text-slate-300 hover:text-white select-none">
                    <input 
                      type="checkbox" 
                      checked={task.completed} 
                      onChange={() => toggleTask('Nazia', task.id)}
                      className="mt-0.5 h-3.5 w-3.5 rounded border-zinc-800 bg-black text-purple-600 focus:ring-0 focus:ring-offset-0" 
                    />
                    <span className={task.completed ? 'line-through text-slate-500' : ''}>{task.text}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Tasks: Mahmood */}
            <div className="bg-[#020205] border border-zinc-900 rounded-2xl p-5 space-y-4">
              <div className="border-b border-zinc-900 pb-2 flex justify-between items-center">
                <span className="text-xs font-bold text-white font-mono uppercase tracking-wider">Mahmood's Queue</span>
                <span className="text-[9px] font-mono font-black uppercase text-purple-400 bg-purple-955/20 px-2 py-0.5 rounded">Approvals</span>
              </div>
              <div className="space-y-3">
                {tasks.Mahmood.map(task => (
                  <label key={task.id} className="flex items-start gap-3 text-xs font-medium cursor-pointer text-slate-300 hover:text-white select-none">
                    <input 
                      type="checkbox" 
                      checked={task.completed} 
                      onChange={() => toggleTask('Mahmood', task.id)}
                      className="mt-0.5 h-3.5 w-3.5 rounded border-zinc-800 bg-black text-purple-600 focus:ring-0 focus:ring-offset-0" 
                    />
                    <span className={task.completed ? 'line-through text-slate-500' : ''}>{task.text}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Section: Quick Access Buttons */}
        <section className="space-y-4">
          <h3 className="text-xs font-mono font-black uppercase tracking-widest text-slate-500 flex items-center gap-2">
            <Layout size={12} className="text-purple-400" /> Operational Resources
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { name: 'Open Command Centre', icon: Layers, url: 'https://docs.google.com/spreadsheets/d/1tD_kHU_1fEwbyQrjnuYATe-CMDVOC9y6RQE8BMUCQ-8/edit?gid=632281061#gid=632281061', roles: ['ADMIN', 'LEADS', 'CANVA'] },
              { name: 'Open Main Drive Folder', icon: Folder, url: 'https://drive.google.com/drive/folders/1AfyXRCzowHAbE0dTl7aSSpjeQYuiUOyr?usp=drive_link', roles: ['ADMIN'] },
              { name: 'Open Campaign Briefs', icon: FileText, url: 'https://drive.google.com/drive/folders/1ACWAcZKcfNy3aPTVYDSZp1NTYUEkH2Iv?usp=drive_link', roles: ['ADMIN', 'LEADS', 'CANVA'] },
              { name: 'Open Canva Drafts', icon: Layout, url: 'https://drive.google.com/drive/folders/1TNe3Q8YFWT-NzhoqgSPg8XVc3Q6k7vlU?usp=drive_link', roles: ['ADMIN', 'CANVA'] },
              { name: 'Open Approved Videos', icon: CheckCircle, url: 'https://drive.google.com/drive/folders/1AsD1hqZn3OoVw9dMdQPYs0OUZ6jCLp62?usp=drive_link', roles: ['ADMIN', 'CANVA'] },
              { name: 'Open Lead Lists', icon: Users, url: 'https://drive.google.com/drive/folders/13tDDcKevHRUhqX6UqY2klPgUaQkKl8Y-?usp=drive_link', roles: ['ADMIN', 'LEADS'] },
              { name: 'Open Client Jobs', icon: Briefcase, url: 'https://drive.google.com/drive/folders/1I3lXz3waKW7rykFpL_nsySn06QRdY74P?usp=drive_link', roles: ['ADMIN', 'LEADS'] },
              { name: 'Open Payment Proofs', icon: FileUp, url: 'https://drive.google.com/drive/folders/14uoMYtguKZXZ4vxOQ62dHzQIct8dhHCa?usp=drive_link', roles: ['ADMIN'] },
              { name: 'Open TikTok Captions', icon: MessageSquare, url: 'https://drive.google.com/drive/folders/1OQv2BC8K3qTwdcWwGOEu4SEiCZDiwYwE?usp=drive_link', roles: ['ADMIN', 'CANVA'] }
            ].filter(btn => workerSession && btn.roles.includes(workerSession.accessLevel))
             .map(btn => (
              <a 
                key={btn.name} 
                href={btn.url} 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3.5 bg-zinc-950 border border-zinc-900 hover:border-purple-500/40 rounded-2xl text-xs font-bold text-slate-300 hover:text-purple-300 hover:shadow-[0_0_15px_rgba(168,85,247,0.08)] transition-all duration-200"
              >
                <div className="flex items-center gap-2.5">
                  <btn.icon size={14} className="text-purple-500" />
                  <span>{btn.name}</span>
                </div>
                <ExternalLink size={10} className="text-slate-600 group-hover:text-purple-400" />
              </a>
            ))}
          </div>
        </section>

        {/* Form and Message Board Panel */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          {/* Submit Work Deliverable */}
          
          <section className="rounded-3xl border border-cyan-500/25 bg-black/60 p-6 shadow-[0_0_30px_rgba(0,240,255,0.08)]">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-900 pb-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
                  NexoraX Lead Discovery Engine
                </p>
                <h3 className="mt-2 text-xl font-black text-white">
                  Find, score and prepare business leads
                </h3>
              </div>
              <span className="rounded-full border border-purple-500/40 bg-purple-950/30 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-purple-200">
                Outreach Accelerator
              </span>
            </div>

            <p className="mt-4 text-sm text-slate-300">
              Enter a business type and town. The engine creates a 10-lead research batch, opens live search routes and prepares AI scoring prompts for the assistant.
            </p>

            <form onSubmit={buildLeadDiscoveryResults} className="mt-5 grid gap-3 md:grid-cols-4">
              <input
                value={leadScout.trade}
                onChange={(e) => setLeadScout({ ...leadScout, trade: e.target.value })}
                placeholder="Business type e.g. joiner"
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              />
              <input
                value={leadScout.town}
                onChange={(e) => setLeadScout({ ...leadScout, town: e.target.value })}
                placeholder="Town / region e.g. Dunfermline"
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              />
              <input
                value={leadScout.batch}
                onChange={(e) => setLeadScout({ ...leadScout, batch: e.target.value })}
                placeholder="Batch number"
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              />
              <button
                type="submit"
                className="rounded-xl border border-cyan-500/40 bg-cyan-950/40 px-4 py-3 font-mono text-xs font-black uppercase tracking-widest text-cyan-100 hover:bg-cyan-900/50"
              >
                Generate 10 Targets
              </button>
            </form>

            <textarea
              value={leadScout.notes}
              onChange={(e) => setLeadScout({ ...leadScout, notes: e.target.value })}
              placeholder="Paste lead notes here: business name, Facebook page, reviews, website status or anything you found..."
              className="mt-3 min-h-[90px] w-full rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-purple-400"
            />

            <div className="mt-5 rounded-2xl border border-cyan-500/20 bg-cyan-950/10 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300">
                    NexoraX Offer Matrix
                  </p>
                  <h4 className="mt-1 text-sm font-black text-white">
                    Pick what to suggest to this lead
                  </h4>
                </div>
                <span className="rounded-full border border-purple-500/30 bg-purple-950/30 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-purple-100">
                  Commission Helper
                </span>
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-3">
                <select
                  value={selectedNexoraXOffer}
                  onChange={(e) => setSelectedNexoraXOffer(e.target.value)}
                  className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400 md:col-span-1"
                >
                  {nexoraxOfferMatrix.map((offer) => (
                    <option key={offer.name} value={offer.name}>
                      {offer.name}
                    </option>
                  ))}
                </select>

                <div className="rounded-xl border border-zinc-800 bg-black/60 p-4 md:col-span-2">
                  <p className="text-xs text-slate-400">
                    Best for: {selectedOfferDetails.bestFor}
                  </p>
                  <p className="mt-2 text-sm font-semibold text-cyan-100">
                    Pitch: {selectedOfferDetails.pitch}
                  </p>
                </div>
              </div>

              <div className="mt-4 grid gap-2 md:grid-cols-3">
                <button
                  type="button"
                  onClick={() => submitOpsAssistantPrompt(`Create a short outreach message for a ${leadScout.trade} business in ${leadScout.town}. Suggest the NexoraX offer: ${selectedOfferDetails.name}. Explain why it fits and keep it professional.`)}
                  className="rounded-xl border border-cyan-500/30 bg-black/60 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-cyan-100 hover:bg-cyan-950/40"
                >
                  Create Outreach
                </button>
                <button
                  type="button"
                  onClick={() => submitOpsAssistantPrompt(`Score this lead idea from 1 to 10: ${leadScout.trade} in ${leadScout.town}. Consider the offer ${selectedOfferDetails.name}. Tell me if it is a good fit and what to check before contacting.`)}
                  className="rounded-xl border border-purple-500/30 bg-black/60 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-purple-100 hover:bg-purple-950/40"
                >
                  Score Offer Fit
                </button>
                <button
                  type="button"
                  onClick={() => submitOpsAssistantPrompt(`Create a proposal angle for ${selectedOfferDetails.name} for a ${leadScout.trade} business in ${leadScout.town}. Include suggested next actions and a simple call-to-action.`)}
                  className="rounded-xl border border-pink-500/30 bg-black/60 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-pink-100 hover:bg-pink-950/40"
                >
                  Proposal Angle
                </button>
              </div>
            </div>

            <div className="mt-4 grid gap-2 md:grid-cols-4">
              <button
                type="button"
                onClick={() => openLeadSearch('maps')}
                className="rounded-xl border border-cyan-500/30 bg-black/60 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-cyan-100 hover:bg-cyan-950/40"
              >
                Open Maps Search
              </button>
              <button
                type="button"
                onClick={() => openLeadSearch('google')}
                className="rounded-xl border border-purple-500/30 bg-black/60 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-purple-100 hover:bg-purple-950/40"
              >
                Open Google Search
              </button>
              <button
                type="button"
                onClick={() => openLeadSearch('facebook')}
                className="rounded-xl border border-blue-500/30 bg-black/60 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-blue-100 hover:bg-blue-950/40"
              >
                Open Facebook Search
              </button>
              <button
                type="button"
                onClick={() => loadLeadIntoAssistant(`Lead Check: ${leadScout.trade} in ${leadScout.town}. Score the lead, suggest the best NexoraX offer, and write a short outreach message. Notes: ${leadScout.notes || 'No notes yet.'}`)}
                className="rounded-xl border border-pink-500/30 bg-black/60 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-pink-100 hover:bg-pink-950/40"
              >
                Ask Assistant
              </button>
            </div>

            {leadScoutResults.length > 0 && (
              <div className="mt-5 grid gap-3 md:grid-cols-2">
                {leadScoutResults.map((lead) => (
                  <div key={lead.title} className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="text-sm font-black text-white">{lead.title}</h4>
                        <p className="mt-2 text-xs leading-relaxed text-slate-400">{lead.why}</p>
                      </div>
                      <span className="rounded-full border border-cyan-500/30 bg-cyan-950/30 px-3 py-1 font-mono text-[10px] text-cyan-100">
                        {lead.priority}% priority
                      </span>
                    </div>

                    <p className="mt-3 text-xs text-purple-200">
                      Suggested offer: {lead.suggestedOffer}
                    </p>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => window.open(`https://www.google.com/search?q=${encodeURIComponent(lead.searchQuery)}`, '_blank', 'noopener,noreferrer')}
                        className="rounded-full border border-cyan-500/30 px-3 py-2 text-[11px] text-cyan-100 hover:bg-cyan-950/40"
                      >
                        Research
                      </button>
                      <button
                        type="button"
                        onClick={() => loadLeadIntoAssistant(lead.assistantPrompt)}
                        className="rounded-full border border-purple-500/30 px-3 py-2 text-[11px] text-purple-100 hover:bg-purple-950/40"
                      >
                        Load into Assistant
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-5 rounded-2xl border border-purple-500/20 bg-purple-950/10 p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-purple-300">
                Coming soon to Lead Discovery
              </p>
              <div className="mt-3 grid gap-2 text-xs text-slate-300 md:grid-cols-2">
                <p>• Live verified business search API</p>
                <p>• One-click CRM lead capture</p>
                <p>• Website / social media quality score</p>
                <p>• Auto proposal draft for each lead</p>
                <p>• Worker activity timeline and login/logout reporting</p>
                <p>• Manager demo dashboard for client companies</p>
              </div>
            </div>
          </section>

<section className="space-y-4 bg-[#020205] border border-zinc-900 rounded-3xl p-6">
            <h3 className="text-xs font-mono font-black uppercase tracking-widest text-slate-500 flex items-center gap-2 mb-2">
              <FileUp size={12} className="text-purple-400" /> Submission Terminal
            </h3>
            
            {submitSuccess && (
              <div className="p-4 bg-emerald-950/20 border border-emerald-500/30 rounded-2xl flex items-center gap-3 text-xs text-emerald-400 font-mono">
                <CheckCircle size={14} />
                <span>Work submitted successfully. Logged in audit registry.</span>
              </div>
            )}

            <form onSubmit={handleFormSubmit} className="space-y-4 text-xs font-mono">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-500 uppercase tracking-wider mb-1.5">Worker Name</label>
                  <select 
                    value={submitForm.workerName}
                    onChange={e => setSubmitForm(prev => ({ ...prev, workerName: e.target.value }))}
                    className="w-full bg-black border border-zinc-800 p-3 rounded-xl text-white outline-none focus:border-purple-500"
                  >
                    <option value="Anjum">Anjum</option>
                    <option value="Nazia">Nazia</option>
                    <option value="Mahmood">Mahmood</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-500 uppercase tracking-wider mb-1.5">Work Type</label>
                  <select 
                    value={submitForm.workType}
                    onChange={e => setSubmitForm(prev => ({ ...prev, workType: e.target.value }))}
                    className="w-full bg-black border border-zinc-800 p-3 rounded-xl text-white outline-none focus:border-purple-500"
                  >
                    <option value="Lead List">Lead List</option>
                    <option value="Canva Layout">Canva Layout</option>
                    <option value="Video Draft">Video Draft</option>
                    <option value="Outreach Copy">Outreach Copy</option>
                    <option value="Payment Verification">Payment Verification</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-slate-500 uppercase tracking-wider mb-1.5">Business / Campaign Name</label>
                <input 
                  type="text" 
                  value={submitForm.businessName}
                  onChange={e => setSubmitForm(prev => ({ ...prev, businessName: e.target.value }))}
                  placeholder="e.g. Edinburgh Roofing Campaign" 
                  className="w-full bg-black border border-zinc-800 p-3 rounded-xl text-white outline-none focus:border-purple-500" 
                  required
                />
              </div>

              <div>
                <label className="block text-slate-500 uppercase tracking-wider mb-1.5">Deliverable Link / Notes</label>
                <textarea 
                  rows={3}
                  value={submitForm.notes}
                  onChange={e => setSubmitForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Link to Canva file or folder, lead counts, or specific instructions..." 
                  className="w-full bg-black border border-zinc-800 p-3 rounded-xl text-white outline-none focus:border-purple-500 resize-none"
                  required
                ></textarea>
              </div>

              <div className="grid grid-cols-2 gap-4 items-end">
                <div>
                  <label className="block text-slate-500 uppercase tracking-wider mb-1.5">Status</label>
                  <select 
                    value={submitForm.status}
                    onChange={e => setSubmitForm(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full bg-black border border-zinc-800 p-3 rounded-xl text-white outline-none focus:border-purple-500"
                  >
                    <option value="Ready for Review">Ready for Review</option>
                    <option value="Needs Help">Needs Help</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
                <button 
                  type="submit" 
                  className="w-full bg-purple-600 hover:bg-purple-500 text-white font-mono font-black uppercase p-3 rounded-xl flex items-center justify-center gap-2 hover:shadow-[0_0_15px_rgba(168,85,247,0.3)] transition-all cursor-pointer"
                >
                  <Send size={12} />
                  <span>Submit Work</span>
                </button>
              </div>
            </form>
          </section>

          {/* Message Board */}
          
          <section className="rounded-3xl border border-cyan-500/30 bg-black/60 p-6 shadow-[0_0_30px_rgba(0,240,255,0.08)]">
            <div className="flex items-center justify-between border-b border-zinc-900 pb-4">
              <div className="flex items-center gap-4">
                <div className={`relative flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-400/50 bg-gradient-to-br from-purple-700 to-cyan-700 shadow-[0_0_25px_rgba(0,240,255,0.25)] ${opsSpeaking ? 'animate-pulse' : ''}`}>
                  <span className="text-xl font-black text-white">NX</span>
                  {opsSpeaking && (
                    <span className="absolute -right-1 -top-1 h-4 w-4 animate-ping rounded-full bg-cyan-300" />
                  )}
                </div>
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
                    NexoraX Ops Assistant
                  </p>
                  <h3 className="mt-2 text-xl font-black text-white">
                    Internal Work Assistant
                  </h3>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                    {opsSpeaking ? 'Voice guidance active' : 'Ready for remote operations'}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className="rounded-full border border-cyan-500/40 bg-cyan-950/30 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-cyan-200">
                  Staff Support
                </span>
                <button
                  type="button"
                  onClick={() => setVoiceMode(voiceMode === 'female' ? 'standard' : 'female')}
                  className="rounded-full border border-purple-500/30 bg-purple-950/20 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-purple-100 hover:bg-purple-900/40"
                >
                  Voice: {voiceMode === 'female' ? 'Female' : 'Standard'}
                </button>
              </div>
            </div>

            <p className="mt-4 text-sm text-slate-300">
              Use the internal assistant to create outreach messages, check leads, improve Canva/TikTok campaign wording, generate hashtags, prepare review notes and build client-ready outputs.
            </p>


            <div className="mt-4 rounded-2xl border border-purple-500/20 bg-purple-950/10 p-4">
              <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-purple-300">
                AI Suggestion Box
              </p>

              <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-4">
                {[
                  'Outreach Mode',
                  'Canva Mode',
                  'Lead Check',
                  'Boss Demo'
                ].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setOpsInput(`${mode}: Help me create a professional NexoraX output for this task.`)}
                    className="rounded-xl border border-purple-500/30 bg-black/60 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-purple-100 transition-all hover:border-cyan-300 hover:bg-cyan-950/30"
                  >
                    {mode}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {opsSuggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => setOpsInput(suggestion)}
                    className="rounded-full border border-cyan-500/30 bg-cyan-950/20 px-3 py-2 text-left text-[11px] font-semibold text-cyan-100 transition-all hover:border-cyan-300 hover:bg-cyan-900/40"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-slate-500">
                Click a suggestion to load it into the assistant box, then press Ask.
              </p>

              <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-4">
                <button
                  type="button"
                  onClick={copyLastAssistantReply}
                  className="rounded-xl border border-cyan-500/30 bg-black/50 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-cyan-100 hover:bg-cyan-950/40"
                >
                  Copy Answer
                </button>
                <button
                  type="button"
                  onClick={speakLastAssistantReply}
                  className="rounded-xl border border-purple-500/30 bg-black/50 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-purple-100 hover:bg-purple-950/40"
                >
                  Read Aloud
                </button>
                <button
                  type="button"
                  onClick={stopAssistantVoice}
                  className="rounded-xl border border-zinc-700 bg-black/50 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-slate-200 hover:bg-zinc-900"
                >
                  Stop Voice
                </button>
                <button
                  type="button"
                  onClick={clearOpsAssistantChat}
                  className="rounded-xl border border-pink-500/30 bg-black/50 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-pink-100 hover:bg-pink-950/40"
                >
                  Clear Chat
                </button>
              </div>
            </div>

            <div className="mt-5 h-64 space-y-3 overflow-y-auto rounded-2xl border border-zinc-900 bg-black/70 p-4">
              {opsMessages.map((msg, index) => (
                <div
                  key={index}
                  className={`rounded-2xl border px-4 py-3 text-sm ${
                    msg.role === 'user'
                      ? 'ml-8 border-purple-500/40 bg-purple-950/30 text-purple-100'
                      : 'mr-8 border-cyan-500/30 bg-cyan-950/20 text-cyan-50'
                  }`}
                >
                  <p className="mb-1 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                    {msg.role === 'user' ? workerSession?.worker || 'Worker' : 'NexoraX Ops Assistant'}
                  </p>
                  <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                </div>
              ))}

              {opsLoading && (
                <div className="mr-8 rounded-2xl border border-cyan-500/30 bg-cyan-950/20 px-4 py-3 text-sm text-cyan-100">
                  NexoraX Ops Assistant is preparing guidance...
                </div>
              )}
            </div>

            <form onSubmit={handleAskOpsAssistant} className="mt-4 flex gap-3">
              <input
                type="text"
                value={opsInput}
                onChange={(e) => setOpsInput(e.target.value)}
                placeholder="Ask for outreach, captions, hashtags, lead checks or task help..."
                className="flex-1 rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              />
              <button
                type="submit"
                disabled={opsLoading}
                className="rounded-xl border border-cyan-500/40 bg-cyan-950/40 px-5 py-3 font-mono text-xs font-black uppercase tracking-widest text-cyan-100 transition-all hover:bg-cyan-900/50 disabled:opacity-50"
              >
                Ask
              </button>
            </form>
          </section>

<section className="space-y-4 bg-[#020205] border border-zinc-900 rounded-3xl p-6 flex flex-col justify-between">
            <div>
              <h3 className="text-xs font-mono font-black uppercase tracking-widest text-slate-500 flex items-center gap-2 mb-4">
                <MessageSquare size={12} className="text-purple-400" /> Operational Message Board
              </h3>
              <div className="space-y-3.5 max-h-[220px] overflow-y-auto pr-1">
                {messages.map(msg => (
                  <div key={msg.id} className="bg-black/40 border border-zinc-950 p-3 rounded-2xl space-y-1">
                    <div className="flex justify-between items-center text-[10px] font-mono">
                      <span className={`font-black uppercase ${
                        msg.sender === 'Mahmood' ? 'text-purple-400' : msg.sender === 'Anjum' ? 'text-cyan-400' : 'text-indigo-400'
                      }`}>{msg.sender}</span>
                      <span className="text-slate-600 flex items-center gap-1">
                        <Clock size={8} /> {msg.time}
                      </span>
                    </div>
                    <p className="text-xs text-slate-300 font-medium leading-relaxed">{msg.content}</p>
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={handleSendMessage} className="mt-4 flex gap-2 font-mono text-xs">
              <input 
                type="text" 
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                placeholder="Broadcast a message to the team..." 
                className="flex-1 bg-black border border-zinc-800 px-4 py-3 rounded-xl text-white outline-none focus:border-purple-500" 
              />
              <button 
                type="submit" 
                className="bg-zinc-900 border border-zinc-800 hover:border-purple-500 hover:bg-purple-950/20 text-purple-400 font-black uppercase px-4 rounded-xl cursor-pointer transition-all"
              >
                Send
              </button>
            </form>
          </section>

        </div>
        
      </div>

          <section className="rounded-3xl border border-cyan-500/25 bg-black/60 p-6 shadow-[0_0_30px_rgba(0,240,255,0.08)]">
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
              NexoraX Worker Success Guide</p>
            <a
              href="/remote-training"
              className="mt-4 inline-flex rounded-xl border border-cyan-500/40 bg-cyan-950/30 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-cyan-100 hover:bg-cyan-900/40"
            >
              Open Remote Training Hub
            </a>
            <p className="hidden">
            </p>
            <h3 className="mt-2 text-xl font-black text-white">
              How to win the task fast
            </h3>
            <div className="mt-5 grid gap-3 md:grid-cols-4">
              {[
                '1. Read your assigned task first',
                '2. Use Lead Discovery or Canva tools',
                '3. Ask Ops Assistant for wording',
                '4. Save the proof/link in Drive',
                '5. Submit work to server',
                '6. Watch review status',
                '7. Fix changes if requested',
                '8. Move to the next task'
              ].map((step) => (
                <div key={step} className="rounded-2xl border border-zinc-800 bg-black/70 p-4 text-sm font-semibold text-cyan-100">
                  {step}
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-3xl border border-purple-500/25 bg-black/60 p-6 shadow-[0_0_30px_rgba(168,85,247,0.08)]">
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-purple-300">
              REMOTE_WORKPLACE_ROADMAP_PATCH
              NexoraX Remote Workplace Expansion Roadmap
            </p>
            <h3 className="mt-2 text-xl font-black text-white">
              More workplace intelligence is being added
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">
              This remote workplace is being expanded into a full out-of-this-universe operations system for remote teams, campaign workers, sales teams and client companies.
            </p>
            <div className="mt-5 grid gap-3 md:grid-cols-3">
              {[
                'Live lead search and CRM capture',
                'Task assignment and manager approval queue',
                'Worker login/logout and activity timeline',
                'Voice notes and assistant-led training',
                'Client company demo dashboard',
                'Auto proposal and campaign pack generator'
              ].map((item) => (
                <div key={item} className="rounded-2xl border border-zinc-800 bg-black/70 p-4 text-sm text-cyan-100">
                  {item}
                </div>
              ))}
            </div>
          </section>


          
        <section className="rounded-3xl border border-emerald-400/40 bg-emerald-950/20 p-5 shadow-2xl shadow-emerald-950/30">
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-emerald-300">Money Desk</p>
              <h2 className="text-2xl font-black text-white">Hot Leads / Follow-Up Today</h2>
              <p className="mt-1 text-sm text-slate-300">
                Chase Smart Quote, QR, TikTok and callback leads quickly before they go cold.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <a href="/smart-quote" target="_blank" rel="noreferrer" className="rounded-full bg-emerald-400 px-4 py-2 text-xs font-black text-slate-950 hover:bg-emerald-300">
                Smart Quote
              </a>
              <a href="/client-preview" target="_blank" rel="noreferrer" className="rounded-full border border-cyan-300/50 px-4 py-2 text-xs font-black text-cyan-100 hover:bg-cyan-400/10">
                Client Preview
              </a>
              <a href="/campaign-launch-pad" target="_blank" rel="noreferrer" className="rounded-full border border-fuchsia-300/50 px-4 py-2 text-xs font-black text-fuchsia-100 hover:bg-fuchsia-400/10">
                Launch Pad
              </a>
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-4">
            <div className="rounded-2xl border border-emerald-300/20 bg-black/40 p-4">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-emerald-300">1. New Lead</p>
              <h3 className="mt-2 text-lg font-black text-white">Reply Fast</h3>
              <p className="mt-2 text-sm text-slate-300">Send a WhatsApp, email or call within 15 minutes.</p>
            </div>

            <div className="rounded-2xl border border-cyan-300/20 bg-black/40 p-4">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-cyan-300">2. Qualify</p>
              <h3 className="mt-2 text-lg font-black text-white">Find The Need</h3>
              <p className="mt-2 text-sm text-slate-300">Website, QR campaign, AI assistant, booking system or launch pack.</p>
            </div>

            <div className="rounded-2xl border border-amber-300/20 bg-black/40 p-4">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-amber-300">3. Offer</p>
              <h3 className="mt-2 text-lg font-black text-white">£199 Preview Deposit</h3>
              <p className="mt-2 text-sm text-slate-300">Send client preview link and ask for starter approval.</p>
            </div>

            <div className="rounded-2xl border border-fuchsia-300/20 bg-black/40 p-4">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-fuchsia-300">4. Close</p>
              <h3 className="mt-2 text-lg font-black text-white">Paid / Follow-Up</h3>
              <p className="mt-2 text-sm text-slate-300">Mark as paid, waiting reply, needs call, or lost.</p>
            </div>
          </div>

          <div className="mt-4 rounded-2xl border border-emerald-300/20 bg-emerald-400/10 p-4 text-sm font-bold text-emerald-100">
            Mahmood rule: every lead gets one fast reply, one follow-up, and one clear offer. No lead sits untouched.
          </div>
        </section>

<section className="rounded-3xl border border-cyan-500/25 bg-black/60 p-6 shadow-[0_0_30px_rgba(0,240,255,0.08)]">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-900 pb-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
                  Server-Saved Workflow Layer
                </p>
                <h3 className="mt-2 text-xl font-black text-white">
                  Assigned Tasks, Work Logs, Reviews & Activity
                </h3>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => loadRemoteServerData(true)}
                  className="rounded-xl border border-cyan-500/30 bg-cyan-950/30 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-cyan-100 hover:bg-cyan-900/40"
                >
                  Refresh Server Data
                </button>

                {workerSession?.accessLevel === 'ADMIN' && (
                  <button
                    type="button"
                    onClick={clearRemoteServerLogs}
                    className="rounded-xl border border-pink-500/30 bg-pink-950/20 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-pink-100 hover:bg-pink-900/40"
                  >
                    Clear Server Logs
                  </button>
                )}
              </div>
            </div>

            {serverNotice && (
              <div className="mt-4 rounded-2xl border border-purple-500/30 bg-purple-950/20 p-4 text-sm text-purple-100">
                {serverNotice}
              </div>
            )}

            <div className="mt-6 rounded-3xl border border-cyan-500/25 bg-gradient-to-br from-cyan-950/20 via-black to-purple-950/20 p-5 shadow-[0_0_35px_rgba(0,240,255,0.08)]">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
                    Remote Mission Control
                  </p>
                  <h3 className="mt-2 text-xl font-black text-white">
                    Today’s workplace command layer
                  </h3>
                  <p className="mt-2 text-sm text-slate-400">
                    Assign work, discover leads, submit outputs, review quality, track worker activity and keep the remote team moving without manual chasing.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={copyDailyRemoteBrief}
                  className="rounded-xl border border-cyan-500/40 bg-cyan-950/30 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-cyan-100 hover:bg-cyan-900/40"
                >
                  Copy Daily Brief
                </button>
              </div>

              <div className="mt-5 grid gap-3 md:grid-cols-4">
                <div className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Open Tasks</p>
                  <p className="mt-2 text-2xl font-black text-cyan-300">{visibleServerTasks.filter((task) => !['Completed', 'Approved'].includes(task.status)).length}</p>
                </div>
                <div className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Pending Reviews</p>
                  <p className="mt-2 text-2xl font-black text-purple-300">{visibleServerSubmissions.filter((item) => item.reviewStatus === 'Pending Review').length}</p>
                </div>
                <div className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Saved Messages</p>
                  <p className="mt-2 text-2xl font-black text-cyan-300">{serverMessages.length}</p>
                </div>
                <div className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Activity Events</p>
                  <p className="mt-2 text-2xl font-black text-purple-300">{visibleServerActivity.length}</p>
                </div>
              </div>

              <div className="mt-5 grid gap-3 md:grid-cols-3">
                <div className="rounded-2xl border border-cyan-500/20 bg-cyan-950/10 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">Worker Flow</p>
                  <p className="mt-2 text-sm text-slate-300">Open task → use Lead Discovery / Assistant → save work in Drive → submit to server → wait for Mahmood review.</p>
                </div>
                <div className="rounded-2xl border border-purple-500/20 bg-purple-950/10 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-purple-300">Manager Flow</p>
                  <p className="mt-2 text-sm text-slate-300">Create task → monitor activity → review submitted work → approve, reject, or request changes.</p>
                </div>
                <div className="rounded-2xl border border-pink-500/20 bg-pink-950/10 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-pink-300">Client Demo Value</p>
                  <p className="mt-2 text-sm text-slate-300">This shows remote staff control, lead generation, AI support, work approval and activity reporting in one system.</p>
                </div>
              </div>
            </div>

            {workerSession?.accessLevel === 'ADMIN' && (
              <section className="mt-6 rounded-3xl border border-purple-500/25 bg-gradient-to-br from-purple-950/20 via-black to-cyan-950/10 p-5 shadow-[0_0_45px_rgba(168,85,247,0.10)]">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.35em] text-purple-300">
                      Incoming Smart Quotes
                    </p>
                    <h3 className="mt-2 text-2xl font-black text-white">
                      Client briefs waiting for Mahmood review
                    </h3>
                    <p className="mt-2 text-sm text-slate-400">
                      Every public Smart Quote submission appears here. Review it, update status, or create tasks for Anjum and Nazia.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={loadSmartQuotes}
                    className="rounded-xl border border-purple-500/40 bg-purple-950/30 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-purple-100 hover:bg-purple-900/40"
                  >
                    Refresh Smart Quotes
                  </button>
                </div>

                {smartQuotes.length === 0 ? (
                  <div className="mt-5 rounded-2xl border border-zinc-800 bg-black/70 p-5 text-sm text-slate-400">
                    No Smart Quote briefs yet. Submit a test brief from /smart-quote and it will appear here.
                  </div>
                ) : (
                  <div className="mt-5 space-y-4">
                    {smartQuotes.slice(0, 10).map((quote) => (
                      <div key={quote.id} className="rounded-3xl border border-zinc-800 bg-black/70 p-5">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div>
                            <h4 className="text-xl font-black text-cyan-200">
                              {quote.businessName || 'Untitled Business'}
                            </h4>
                            <p className="mt-1 text-xs text-slate-400">
                              Ref: {quote.reference} • {quote.industry || 'No industry'} • {quote.town || 'No town'} • {quote.createdAt ? new Date(quote.createdAt).toLocaleString() : ''}
                            </p>
                          </div>

                          <span className="rounded-full border border-purple-500/40 bg-purple-950/30 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-purple-100">
                            {quote.status || 'New'}
                          </span>
                        </div>

                        <div className="mt-4 grid gap-3 md:grid-cols-3">
                          <div className="rounded-2xl border border-zinc-800 bg-black p-4">
                            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Contact</p>
                            <p className="mt-2 text-sm text-white">{quote.contactName || 'No name'}</p>
                            <p className="text-xs text-slate-400">{quote.email || 'No email'}</p>
                            <p className="text-xs text-slate-400">{quote.phone || 'No phone'}</p>
                          </div>

                          <div className="rounded-2xl border border-zinc-800 bg-black p-4">
                            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Budget / Urgency</p>
                            <p className="mt-2 text-sm text-white">{quote.budget || 'Not given'}</p>
                            <p className="text-xs text-cyan-300">{quote.urgency || 'No urgency'}</p>
                          </div>

                          <div className="rounded-2xl border border-zinc-800 bg-black p-4">
                            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Recommendation</p>
                            <p className="mt-2 text-sm text-white">{quote.recommendation?.mainService || 'Review required'}</p>
                            <p className="text-xs text-cyan-300">Score: {quote.recommendation?.overall || '-'}%</p>
                          </div>
                        </div>

                        <div className="mt-4 rounded-2xl border border-cyan-500/20 bg-cyan-950/10 p-4">
                          <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">Requested Services</p>
                          <p className="mt-2 text-sm text-white">{(quote.services || []).join(', ') || 'No services selected'}</p>
                          {quote.goals && <p className="mt-3 text-sm text-slate-300">Goals: {quote.goals}</p>}
                          {quote.notes && <p className="mt-2 text-sm text-slate-400">Notes: {quote.notes}</p>}
                        </div>

                        <textarea
                          className="mt-4 min-h-[80px] w-full rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-purple-400"
                          placeholder="Mahmood review notes..."
                          value={smartQuoteNotes[quote.id] ?? quote.adminNotes ?? ''}
                          onChange={(e) =>
                            setSmartQuoteNotes((prev) => ({
                              ...prev,
                              [quote.id]: e.target.value
                            }))
                          }
                        />

                        <div className="mt-4 flex flex-wrap gap-2">
                          {['Under Review', 'Preview Requested', 'Callback Requested', 'Approved', 'Archived'].map((status) => (
                            <button
                              key={status}
                              type="button"
                              onClick={() => updateSmartQuoteStatus(quote.id, status)}
                              className="rounded-full border border-cyan-500/30 px-3 py-2 text-[11px] text-cyan-100 hover:bg-cyan-950/40"
                            >
                              {status}
                            </button>
                          ))}

                          <button
                            type="button"
                            onClick={() => createTaskFromSmartQuote(quote, 'lead')}
                            className="rounded-full border border-purple-500/40 bg-purple-950/20 px-3 py-2 text-[11px] text-purple-100 hover:bg-purple-900/40"
                          >
                            Create Anjum Task
                          </button>

                          <button
                            type="button"
                            onClick={() => createTaskFromSmartQuote(quote, 'creative')}
                            className="rounded-full border border-pink-500/40 bg-pink-950/20 px-3 py-2 text-[11px] text-pink-100 hover:bg-pink-900/40"
                          >
                            Create Nazia Task
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {workerSession?.accessLevel === 'ADMIN' && (
              <section id="incoming-launch-campaigns" className="mt-6 rounded-3xl border border-pink-500/25 bg-gradient-to-br from-pink-950/20 via-black to-cyan-950/10 p-5 shadow-[0_0_45px_rgba(236,72,153,0.10)]">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.35em] text-pink-300">
                      Incoming Launch Campaigns
                    </p>
                    <h3 className="mt-2 text-2xl font-black text-white">
                      TikTok campaigns waiting for Mahmood review
                    </h3>
                    <p className="mt-2 text-sm text-slate-400">
                      Saved Launch Pad campaigns appear here. Review the video, tracking URL, budget and KPI plan, then create Nazia or Anjum tasks.
                    </p>
                  </div>

                  <a
                    href="/remote-workplace?v=launch-panel-reload#incoming-launch-campaigns"
                    className="rounded-xl border border-pink-500/40 bg-pink-950/30 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-pink-100 hover:bg-pink-900/40"
                  >
                    Reload Launch Panel
                  </a>
                </div>

                {launchCampaigns.length === 0 ? (
                  <div className="mt-5 rounded-2xl border border-zinc-800 bg-black/70 p-5 text-sm text-slate-400">
                    No Launch Pad campaigns yet. Save a campaign from /campaign-launch-pad and it will appear here.
                  </div>
                ) : (
                  <div className="mt-5 space-y-4">
                    {launchCampaigns.slice(0, 10).map((campaign) => (
                      <div key={campaign.id} className="rounded-3xl border border-zinc-800 bg-black/70 p-5">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div>
                            <h4 className="text-xl font-black text-cyan-200">
                              {campaign.campaignName || 'Untitled Launch Campaign'}
                            </h4>
                            <p className="mt-1 text-xs text-slate-400">
                              Ref: {campaign.reference} • {campaign.platform || 'No platform'} • {campaign.objective || 'No objective'} • {campaign.createdAt ? new Date(campaign.createdAt).toLocaleString() : ''}
                            </p>
                          </div>

                          <span className="rounded-full border border-pink-500/40 bg-pink-950/30 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-pink-100">
                            {campaign.status || 'Draft'}
                          </span>
                        </div>

                        <div className="mt-4 grid gap-3 md:grid-cols-4">
                          <div className="rounded-2xl border border-zinc-800 bg-black p-4">
                            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Budget</p>
                            <p className="mt-2 text-sm text-white">{campaign.dailyBudget || 'Budget not set'}</p>
                            <p className="text-xs text-cyan-300">Total: {campaign.totalBudget || 'Not set'}</p>
                          </div>

                          <div className="rounded-2xl border border-zinc-800 bg-black p-4">
                            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Video</p>
                            <p className="mt-2 text-sm text-white">{campaign.videoType || 'No video type'}</p>
                            <p className="break-all text-xs text-slate-400">{campaign.videoLink || 'No video link yet'}</p>
                          </div>

                          <div className="rounded-2xl border border-zinc-800 bg-black p-4">
                            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Current Results</p>
                            <p className="mt-2 text-sm text-white">Spend: £{campaign.spend || '0'}</p>
                            <p className="text-xs text-slate-400">Clicks: {campaign.clicks || '0'} • Leads: {campaign.leads || '0'}</p>
                          </div>

                          <div className="rounded-2xl border border-zinc-800 bg-black p-4">
                            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">KPI</p>
                            <p className="mt-2 text-sm text-cyan-200">CTR: {campaign.metrics?.ctr || '0.00'}%</p>
                            <p className="text-xs text-purple-300">CPC: £{campaign.metrics?.cpc || '0.00'}</p>
                          </div>
                        </div>

                        <div className="mt-4 rounded-2xl border border-cyan-500/20 bg-cyan-950/10 p-4">
                          <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">Tracking URL for TikTok Destination URL</p>
                          <p className="mt-2 break-all text-xs text-cyan-100">{campaign.trackingUrl || 'No tracking URL'}</p>
                        </div>

                        <div className="mt-4 grid gap-3 md:grid-cols-2">
                          <div className="rounded-2xl border border-purple-500/20 bg-purple-950/10 p-4">
                            <p className="font-mono text-[10px] uppercase tracking-widest text-purple-300">Caption</p>
                            <p className="mt-2 text-sm text-slate-300">{campaign.caption || 'No caption saved.'}</p>
                            <p className="mt-2 text-xs text-slate-400">{campaign.hashtags || ''}</p>
                          </div>

                          <div className="rounded-2xl border border-pink-500/20 bg-pink-950/10 p-4">
                            <p className="font-mono text-[10px] uppercase tracking-widest text-pink-300">Launch Notes</p>
                            <p className="mt-2 text-sm text-slate-300">{campaign.notes || 'No notes saved.'}</p>
                            <p className="mt-2 text-xs text-slate-400">{campaign.audience || ''}</p>
                          </div>
                        </div>

                        <textarea
                          className="mt-4 min-h-[80px] w-full rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-pink-400"
                          placeholder="Mahmood launch campaign review notes..."
                          value={launchCampaignNotes[campaign.id] ?? campaign.notes ?? ''}
                          onChange={(e) =>
                            setLaunchCampaignNotes((prev) => ({
                              ...prev,
                              [campaign.id]: e.target.value
                            }))
                          }
                        />

                        <div className="mt-4 flex flex-wrap gap-2">
                          {['Draft', 'Ready for Review', 'Ready to Launch', 'Live', 'Paused', 'Completed', 'Archived'].map((status) => (
                            <button
                              key={status}
                              type="button"
                              onClick={() => updateLaunchCampaignStatus(campaign.id, status)}
                              className="rounded-full border border-cyan-500/30 px-3 py-2 text-[11px] text-cyan-100 hover:bg-cyan-950/40"
                            >
                              {status}
                            </button>
                          ))}

                          <button
                            type="button"
                            onClick={() => createTaskFromLaunchCampaign(campaign, 'video')}
                            className="rounded-full border border-pink-500/40 bg-pink-950/20 px-3 py-2 text-[11px] text-pink-100 hover:bg-pink-900/40"
                          >
                            Create Nazia Video Task
                          </button>

                          <button
                            type="button"
                            onClick={() => createTaskFromLaunchCampaign(campaign, 'tracking')}
                            className="rounded-full border border-purple-500/40 bg-purple-950/20 px-3 py-2 text-[11px] text-purple-100 hover:bg-purple-900/40"
                          >
                            Create Anjum Tracking Task
                          </button>

                          <a
                            href={campaign.trackingUrl || '/smart-quote'}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-full border border-cyan-500/40 px-3 py-2 text-[11px] text-cyan-100 hover:bg-cyan-950/40"
                          >
                            Test Tracking Link
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {workerSession?.accessLevel === 'ADMIN' && (
              <section id="incoming-qr-campaigns" className="mt-6 rounded-3xl border border-cyan-500/25 bg-gradient-to-br from-cyan-950/20 via-black to-purple-950/10 p-5 shadow-[0_0_45px_rgba(0,240,255,0.10)]">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
                      Incoming QR Campaigns
                    </p>
                    <h3 className="mt-2 text-2xl font-black text-white">
                      QR campaigns waiting for Mahmood review
                    </h3>
                    <p className="mt-2 text-sm text-slate-400">
                      Every saved QR Campaign appears here. Review the offer, destination, poster copy and TikTok caption, then create Anjum or Nazia tasks.
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <a
                      href="/remote-workplace?v=qr-panel-reload#incoming-qr-campaigns"
                      className="rounded-xl border border-cyan-500/40 bg-cyan-950/30 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-cyan-100 hover:bg-cyan-900/40"
                    >
                      Reload QR Panel
                    </a>

                    <a
                      href="/qr-campaign"
                      className="rounded-xl border border-purple-500/40 bg-purple-950/30 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-purple-100 hover:bg-purple-900/40"
                    >
                      Open QR Generator
                    </a>
                  </div>
                </div>

                {qrCampaigns.length === 0 ? (
                  <div className="mt-5 rounded-2xl border border-zinc-800 bg-black/70 p-5 text-sm text-slate-400">
                    No QR Campaigns yet. Save a test campaign from /qr-campaign and it will appear here.
                  </div>
                ) : (
                  <div className="mt-5 space-y-4">
                    {qrCampaigns.slice(0, 12).map((campaign) => {
                      const campaignData = campaign.campaign || {};

                      return (
                        <div key={campaign.id} className="rounded-3xl border border-zinc-800 bg-black/70 p-5">
                          <div className="flex flex-wrap items-start justify-between gap-4">
                            <div>
                              <h4 className="text-xl font-black text-cyan-200">
                                {campaign.businessName || 'Untitled QR Campaign'}
                              </h4>
                              <p className="mt-1 text-xs text-slate-400">
                                Ref: {campaign.reference} • {campaign.businessType || 'No type'} • {campaign.town || 'No town'} • {campaign.createdAt ? new Date(campaign.createdAt).toLocaleString() : ''}
                              </p>
                            </div>

                            <span className="rounded-full border border-purple-500/40 bg-purple-950/30 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-purple-100">
                              {campaign.status || 'New'}
                            </span>
                          </div>

                          <div className="mt-4 grid gap-3 md:grid-cols-3">
                            <div className="rounded-2xl border border-zinc-800 bg-black p-4">
                              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Goal</p>
                              <p className="mt-2 text-sm text-white">{campaign.goal || 'No goal'}</p>
                              <p className="text-xs text-cyan-300">{campaign.offerTitle || 'No offer title'}</p>
                            </div>

                            <div className="rounded-2xl border border-zinc-800 bg-black p-4">
                              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Destination</p>
                              <p className="mt-2 break-all text-xs text-cyan-200">{campaign.destination || campaign.destinationUrl || 'No destination'}</p>
                            </div>

                            <div className="rounded-2xl border border-zinc-800 bg-black p-4">
                              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Contact</p>
                              <p className="mt-2 text-sm text-white">{campaign.contactName || 'No contact'}</p>
                              <p className="text-xs text-slate-400">{campaign.email || 'No email'}</p>
                              <p className="text-xs text-slate-400">{campaign.phone || 'No phone'}</p>
                            </div>
                          </div>

                          <div className="mt-4 grid gap-3 md:grid-cols-2">
                            <div className="rounded-2xl border border-cyan-500/20 bg-cyan-950/10 p-4">
                              <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">Poster / Flyer Copy</p>
                              <p className="mt-2 text-sm font-bold text-white">{campaignData.headline || campaign.offerTitle || 'QR Campaign'}</p>
                              <p className="mt-2 text-sm text-slate-300">{campaignData.posterCopy || campaign.offerDetails || 'No poster copy saved.'}</p>
                              <p className="mt-2 text-sm font-bold text-cyan-200">{campaignData.cta || 'Scan to act'}</p>
                            </div>

                            <div className="rounded-2xl border border-purple-500/20 bg-purple-950/10 p-4">
                              <p className="font-mono text-[10px] uppercase tracking-widest text-purple-300">TikTok / Social Caption</p>
                              <p className="mt-2 text-sm text-slate-300">{campaignData.tikTokCaption || 'No caption saved.'}</p>
                            </div>
                          </div>

                          {campaign.adminNotes && (
                            <p className="mt-4 text-xs text-cyan-300">
                              Mahmood notes: {campaign.adminNotes}
                            </p>
                          )}

                          <textarea
                            className="mt-4 min-h-[80px] w-full rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
                            placeholder="Mahmood QR campaign review notes..."
                            value={qrCampaignNotes[campaign.id] ?? campaign.adminNotes ?? ''}
                            onChange={(e) =>
                              setQrCampaignNotes((prev) => ({
                                ...prev,
                                [campaign.id]: e.target.value
                              }))
                            }
                          />

                          <div className="mt-4 flex flex-wrap gap-2">
                            {['Under Review', 'Design Needed', 'Approved', 'Archived'].map((status) => (
                              <button
                                key={status}
                                type="button"
                                onClick={() => updateQrCampaignStatus(campaign.id, status)}
                                className="rounded-full border border-cyan-500/30 px-3 py-2 text-[11px] text-cyan-100 hover:bg-cyan-950/40"
                              >
                                {status}
                              </button>
                            ))}

                            <button
                              type="button"
                              onClick={() => createTaskFromQrCampaign(campaign, 'lead')}
                              className="rounded-full border border-purple-500/40 bg-purple-950/20 px-3 py-2 text-[11px] text-purple-100 hover:bg-purple-900/40"
                            >
                              Create Anjum Task
                            </button>

                            <button
                              type="button"
                              onClick={() => createTaskFromQrCampaign(campaign, 'creative')}
                              className="rounded-full border border-pink-500/40 bg-pink-950/20 px-3 py-2 text-[11px] text-pink-100 hover:bg-pink-900/40"
                            >
                              Create Nazia Task
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            )}

            {workerSession?.accessLevel === 'ADMIN' && (
              <form onSubmit={createRemoteTask} className="mt-6 rounded-3xl border border-purple-500/25 bg-purple-950/10 p-5">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-purple-300">
                  Mahmood Task Creator
                </p>

                <div className="mt-4 grid gap-3 md:grid-cols-3">
                  <input
                    value={remoteTaskForm.title}
                    onChange={(e) => setRemoteTaskForm({ ...remoteTaskForm, title: e.target.value })}
                    placeholder="Task title"
                    className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
                  />

                  <select
                    value={remoteTaskForm.assignedTo}
                    onChange={(e) => setRemoteTaskForm({
                      ...remoteTaskForm,
                      assignedTo: e.target.value,
                      assignedRole: e.target.value === 'Nazia' ? 'CANVA' : 'LEADS'
                    })}
                    className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
                  >
                    <option value="Anjum">Anjum — Leads</option>
                    <option value="Nazia">Nazia — Canva/TikTok</option>
                  </select>

                  <select
                    value={remoteTaskForm.priority}
                    onChange={(e) => setRemoteTaskForm({ ...remoteTaskForm, priority: e.target.value })}
                    className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
                  >
                    <option>Low</option>
                    <option>Medium</option>
                    <option>High</option>
                    <option>Urgent</option>
                  </select>

                  <input
                    value={remoteTaskForm.category}
                    onChange={(e) => setRemoteTaskForm({ ...remoteTaskForm, category: e.target.value })}
                    placeholder="Category"
                    className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
                  />

                  <input
                    value={remoteTaskForm.dueDate}
                    onChange={(e) => setRemoteTaskForm({ ...remoteTaskForm, dueDate: e.target.value })}
                    placeholder="Due date e.g. today / Friday"
                    className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
                  />

                  <button
                    type="submit"
                    className="rounded-xl border border-cyan-500/40 bg-cyan-950/40 px-4 py-3 font-mono text-xs font-black uppercase tracking-widest text-cyan-100 hover:bg-cyan-900/50"
                  >
                    Create Task
                  </button>
                </div>

                <textarea
                  value={remoteTaskForm.instructions}
                  onChange={(e) => setRemoteTaskForm({ ...remoteTaskForm, instructions: e.target.value })}
                  placeholder="Task instructions for worker..."
                  className="mt-3 min-h-[90px] w-full rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-purple-400"
                />
              </form>
            )}

            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              <section className="rounded-3xl border border-zinc-800 bg-black/50 p-5">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-300">
                  Assigned Tasks
                </p>

                <div className="mt-4 space-y-3">
                  {visibleServerTasks.length === 0 ? (
                    <p className="text-sm text-slate-500">No assigned tasks yet.</p>
                  ) : (
                    visibleServerTasks.map((task: any) => (
                      <div key={task.id} className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <h4 className="font-black text-white">{task.title}</h4>
                            <p className="mt-1 text-xs text-slate-400">
                              {task.assignedTo} • {task.category} • {task.priority} • Due: {task.dueDate || 'Not set'}
                            </p>
                          </div>
                          <span className="rounded-full border border-cyan-500/30 bg-cyan-950/30 px-3 py-1 font-mono text-[10px] text-cyan-100">
                            {task.status}
                          </span>
                        </div>

                        {task.instructions && (
                          <p className="mt-3 text-sm leading-relaxed text-slate-300">{task.instructions}</p>
                        )}

                        {task.workerNotes && (
                          <p className="mt-3 text-xs text-purple-200">Worker notes: {task.workerNotes}</p>
                        )}

                        {task.reviewNotes && (
                          <p className="mt-3 text-xs text-cyan-200">Review notes: {task.reviewNotes}</p>
                        )}

                        <div className="mt-4 flex flex-wrap gap-2">
                          {workerSession?.accessLevel !== 'ADMIN' && (
                            <>
                              <button
                                type="button"
                                onClick={() => updateRemoteTask(task.id, { status: 'In Progress' })}
                                className="rounded-full border border-cyan-500/30 px-3 py-2 text-[11px] text-cyan-100 hover:bg-cyan-950/40"
                              >
                                Mark In Progress
                              </button>
                              <button
                                type="button"
                                onClick={() => updateRemoteTask(task.id, { status: 'Ready for Review' })}
                                className="rounded-full border border-purple-500/30 px-3 py-2 text-[11px] text-purple-100 hover:bg-purple-950/40"
                              >
                                Ready for Review
                              </button>
                            </>
                          )}

                          {workerSession?.accessLevel === 'ADMIN' && (
                            <>
                              {['Needs Changes', 'Approved', 'Completed'].map((status) => (
                                <button
                                  key={status}
                                  type="button"
                                  onClick={() => updateRemoteTask(task.id, { status })}
                                  className="rounded-full border border-purple-500/30 px-3 py-2 text-[11px] text-purple-100 hover:bg-purple-950/40"
                                >
                                  {status}
                                </button>
                              ))}
                            </>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </section>

              <section className="rounded-3xl border border-zinc-800 bg-black/50 p-5">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-purple-300">
                  Server Submit Work
                </p>

                <form onSubmit={submitServerWork} className="mt-4 space-y-3">
                  <select
                    value={serverWorkForm.workType}
                    onChange={(e) => setServerWorkForm({ ...serverWorkForm, workType: e.target.value })}
                    className="w-full rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
                  >
                    <option>Lead List</option>
                    <option>Canva Draft</option>
                    <option>TikTok Caption</option>
                    <option>Campaign Brief</option>
                    <option>Client Job</option>
                    <option>Other</option>
                  </select>

                  <input
                    value={serverWorkForm.businessOrCampaignName}
                    onChange={(e) => setServerWorkForm({ ...serverWorkForm, businessOrCampaignName: e.target.value })}
                    placeholder="Business / campaign name"
                    className="w-full rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
                  />

                  <textarea
                    value={serverWorkForm.deliverableLinkOrNotes}
                    onChange={(e) => setServerWorkForm({ ...serverWorkForm, deliverableLinkOrNotes: e.target.value })}
                    placeholder="Paste link, notes, lead count, Canva URL or work details..."
                    className="min-h-[110px] w-full rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-purple-400"
                  />

                  <button
                    type="submit"
                    className="w-full rounded-xl border border-cyan-500/40 bg-cyan-950/40 px-4 py-3 font-mono text-xs font-black uppercase tracking-widest text-cyan-100 hover:bg-cyan-900/50"
                  >
                    Submit Work to Server
                  </button>
                </form>
              </section>
            </div>

            <section className="mt-6 rounded-3xl border border-zinc-800 bg-black/50 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-300">
                  Submitted Work Log
                </p>

                {workerSession?.accessLevel === 'ADMIN' && (
                  <button
                    type="button"
                    onClick={() => setShowArchivedSubmissions(!showArchivedSubmissions)}
                    className="rounded-xl border border-purple-500/30 bg-purple-950/20 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-purple-100 hover:bg-purple-900/40"
                  >
                    {showArchivedSubmissions ? 'Hide Archived' : 'Show Archived'}
                  </button>
                )}
              </div>

              <div className="mt-4 grid gap-3">
                {visibleServerSubmissions.length === 0 ? (
                  <p className="text-sm text-slate-500">No submitted work yet.</p>
                ) : (
                  visibleServerSubmissions.map((item: any) => (
                    <div key={item.id} className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                      <div className="flex flex-wrap justify-between gap-3">
                        <div>
                          <h4 className="font-black text-white">{item.businessOrCampaignName || 'Untitled submission'}</h4>
                          <p className="mt-1 text-xs text-slate-400">
                            {item.workerName} • {item.workType} • {new Date(item.timestamp).toLocaleString()}
                          </p>
                        </div>
                        <span className="rounded-full border border-purple-500/30 bg-purple-950/30 px-3 py-1 font-mono text-[10px] text-purple-100">
                          {item.reviewStatus}
                        </span>
                      </div>

                      <p className="mt-3 whitespace-pre-wrap text-sm text-slate-300">{item.deliverableLinkOrNotes}</p>

                      {item.adminNotes && (
                        <p className="mt-3 text-xs text-cyan-200">Mahmood notes: {item.adminNotes}</p>
                      )}

                      {workerSession?.accessLevel === 'ADMIN' && (
                        <div className="mt-4 space-y-3">
                          <input
                            value={adminReviewNotes[item.id] || ''}
                            onChange={(e) => setAdminReviewNotes({ ...adminReviewNotes, [item.id]: e.target.value })}
                            placeholder="Admin review notes..."
                            className="w-full rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
                          />

                          <div className="flex flex-wrap gap-2">
                            {['Needs Changes', 'Approved for Mahmood', 'Approved', 'Rejected', 'Archived'].map((status) => (
                              <button
                                key={status}
                                type="button"
                                onClick={() => reviewServerSubmission(item.id, status)}
                                className="rounded-full border border-cyan-500/30 px-3 py-2 text-[11px] text-cyan-100 hover:bg-cyan-950/40"
                              >
                                {status}
                              </button>
                            ))}

                            <button
                              type="button"
                              onClick={() => deleteServerSubmission(item.id)}
                              className="rounded-full border border-pink-500/40 px-3 py-2 text-[11px] text-pink-100 hover:bg-pink-950/40"
                            >
                              Delete Test
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </section>

            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              <section className="rounded-3xl border border-zinc-800 bg-black/50 p-5">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-purple-300">
                  Saved Operations Message Board
                </p>

                <div className="mt-4 max-h-72 space-y-3 overflow-y-auto">
                  {serverMessages.length === 0 ? (
                    <p className="text-sm text-slate-500">No saved messages yet.</p>
                  ) : (
                    serverMessages.map((msg: any) => (
                      <div key={msg.id} className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                        <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">
                          {msg.workerName} • {new Date(msg.timestamp).toLocaleString()}
                        </p>
                        <p className="mt-2 text-sm text-white">{msg.messageText}</p>
                      </div>
                    ))
                  )}
                </div>

                <form onSubmit={sendServerMessage} className="mt-4 flex gap-2">
                  <input
                    value={serverMessageText}
                    onChange={(e) => setServerMessageText(e.target.value)}
                    placeholder="Write saved server message..."
                    className="flex-1 rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
                  />
                  <button
                    type="submit"
                    className="rounded-xl border border-purple-500/40 bg-purple-950/40 px-4 py-3 font-mono text-xs font-black uppercase tracking-widest text-purple-100 hover:bg-purple-900/50"
                  >
                    Send
                  </button>
                </form>
              </section>

              <section className="rounded-3xl border border-zinc-800 bg-black/50 p-5">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-300">
                  Worker Activity Timeline
                </p>

                <div className="mt-4 max-h-80 space-y-3 overflow-y-auto">
                  {visibleServerActivity.length === 0 ? (
                    <p className="text-sm text-slate-500">No activity recorded yet.</p>
                  ) : (
                    visibleServerActivity.slice(0, 50).map((event: any) => (
                      <div key={event.id} className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                        <div className="flex justify-between gap-3">
                          <p className="font-black text-white">{event.eventType}</p>
                          <p className="text-xs text-slate-500">{new Date(event.timestamp).toLocaleString()}</p>
                        </div>
                        <p className="mt-1 text-xs text-cyan-300">{event.workerName} • {event.accessLevel}</p>
                        <p className="mt-2 text-sm text-slate-300">{event.details}</p>
                      </div>
                    ))
                  )}
                </div>
              </section>
            </div>
          </section>

    </div>
  );
}
